import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { v2Tone } from '../theme/theme';
import {
  SKU_LEN,
  extractSkuSegments,
  buildResolverArgs,
  resolveVariantSku,
  variantAxisKinds,
  prefillFromLiveValues,
  preisVonGeschwistern,
} from '../utils/skuComposition';
import CloseIcon from '@mui/icons-material/Close';
import { TOUCH_TARGET_COARSE } from '../theme/tokens';
import MobileSheet from './common/MobileSheet';
import DisabledReason from './common/DisabledReason';
import BildKandidatenListe from './common/BildKandidatenListe';
import { baueAnreicherungsFelder } from './anreicherungNutzlast';

import VerticalSelect from './common/VerticalSelect';

const API = process.env.REACT_APP_API_BASE_URL;

/* ----------------------------- helpers ----------------------------- */

/**
 * Generate a provisional SKU for basic-add mode products.
 *
 * Format: `PROV-<vendor-slug>-<8-char-random>` where:
 *   - vendor-slug = uppercased + non-alphanumeric stripped, truncated to 6 chars
 *   - empty / null / undefined vendor → fallback `UNK`
 *   - random suffix = 8 chars from base36 (`[A-Z0-9]`), padded with `X` if short
 *
 * @deprecated basic-add wizard now creates active products via POST /api/products v2
 *   with allocator-issued 12-digit SKUs. This function is no longer called from
 *   handleBasicSubmit. Export kept for the test in
 *   frontend/src/components/common/__tests__/generateProvisionalSku.test.js —
 *   remove in a follow-up PR once no callers remain.
 *
 * @param {string|null|undefined} vendorKey - vendor name or id
 * @returns {string} matching /^PROV-[A-Z]{1,6}-[A-Z0-9]{8}$/
 */
export const generateProvisionalSku = (vendorKey) => {
  const cleaned = String(vendorKey || '').replace(/[^a-z0-9]/gi, '');
  const slug = (cleaned || 'unk').slice(0, 6).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 10).toUpperCase().padEnd(8, 'X');
  return `PROV-${slug}-${rand}`;
};

// Die Kaffee-Ersatzliste: die vier Kategorien, die Basic-Add fuer `brewout`
// anbietet, wenn der Register-Abruf gescheitert ist (`categoriesStatus ===
// 'error'`, `categories` leer).
//
// 🔴 Sie ist die EINE Quelle fuer ANZEIGE und RIEGEL. Bis zum 2026-10-13 standen
// die vier Namen nur im Select, waehrend der Riegel in `handleBasicSubmit` gegen
// die — dann leere — Registerliste pruefte: das Feld BOT die Werte an und die
// Anlage LEHNTE sie mit einem falschen Fehlertext ab (Regel-16-Runde-2-Blocker 1,
// Sackgasse ohne Ausweg). `categories` wird ausschliesslich an den drei Wegen des
// Abruf-Effekts gesetzt — kein Weg fuellt die Ersatzliste hinein.
const KAFFEE_ERSATZKATEGORIEN = [
  'Zubehör und Ersatzteile',
  'Kaffee-Mühlen',
  'Espressomaschine',
  'Misc',
];

const cartesian = (arrays) =>
  arrays.reduce((acc, curr) => acc.flatMap(a => curr.map(b => [...a, b])), [[]]);

const pad2  = (v) => String(v ?? '').padStart(2, '0');
const isDigits = (s) => /^[0-9]+$/.test(s || '');

/**
 * RFC 4122 v4 UUID — uses crypto.randomUUID() when available (HTTPS + modern browsers),
 * falls back to a manual byte-level implementation. Both paths produce a valid UUID
 * that passes the `uuid.UUID(norm["idempotency_key"])` validation in
 * `backend/products_v2.py`.
 * The old basic-mode fallback (`basic-${Date.now()}-${Math.random()}`) was NOT a UUID
 * and caused 400 errors from the v2 validator on insecure contexts.
 */
const makeUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Manual RFC4122 v4 fallback — no external dep, works in insecure contexts.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0,4).join('')}-${hex.slice(4,6).join('')}-${hex.slice(6,8).join('')}-${hex.slice(8,10).join('')}-${hex.slice(10,16).join('')}`;
};

/** Find code helpers from mapping lists returned by your API */
const codeBy = (list, key, val, codeKey) => {
  if (!val) return '';
  const row = list.find(r => String(r[key]) === String(val));
  return row ? String(row[codeKey] || '') : '';
};

const normalizeSkuString = (sku) => {
  if (!sku) return '';
  return String(sku).replace(/\D/g, '');
};

const rowByCode = (list, codeKey, code) => {
  if (!code) return null;
  return list.find(r => String(r[codeKey]) === String(code)) || null;
};

const SKU_PREVIEW_LIMIT = 6;
const LEN_VENDOR = 2;

const validSegment = (value, len) => Boolean(value) && isDigits(value) && String(value).length === len;
const computeNextMappingCode = (rows, key) => {
  const used = new Set(
    (rows || [])
      .map((row) => parseInt(String(row?.[key] ?? '').trim(), 10))
      .filter((n) => Number.isFinite(n))
  );
  for (let i = 0; i < 100; i += 1) {
    if (!used.has(i)) return pad2(i);
  }
  return pad2(0);
};

const AXIS_ORDER = ['model', 'size', 'color'];
const AXIS_INFO = {
  model: { defaultLabel: 'Model', valueKey: 'model', codeKey: 'model_code', addType: 'model' },
  size: { defaultLabel: 'Size', valueKey: 'size', codeKey: 'size_code', addType: 'size' },
  color: { defaultLabel: 'Color', valueKey: 'color', codeKey: 'color_code', addType: 'color' },
};

// Mirrors backend `_PLACEHOLDER_AXIS_NAMES` (products_v2.py) — Shopify's
// single-variant default option name, meaning "no real option axis exists
// yet" on the live product.
const PLACEHOLDER_AXIS_NAMES = new Set(['title', 'default title']);

/** Traegt diese Achse einen ECHTEN Namen — oder ist sie Shopifys Platzhalter?
 *
 *  🔴 Als Funktion, nicht zweimal wortgleich (Regel-15-Warnung W-2): die
 *  Bedingung entscheidet an ZWEI Stellen dasselbe — ob `axisDefinitions` den
 *  Live-Zweig nimmt, und ob das Zusatzachsen-Panel erscheinen darf. Die ganze
 *  Reparatur des Blockers B1 ruht auf ihrer Gleichheit; eine Kopie driftet.
 */
const istEchteAchse = (name) => (
  !PLACEHOLDER_AXIS_NAMES.has(String(name || '').trim().toLowerCase())
);

/** Der Wertschluessel einer Achse OHNE POG-Kind.
 *
 *  🔴 Konstant, nicht `v${i}`: er wird nur INNERHALB einer Achse gelesen
 *  (`getOptionLabel`, `cartesian`), und die Zuordnung im Rumpf laeuft
 *  positionell ueber `activeAxes[i]`. Mit einem indexabhaengigen Schluessel
 *  koennte der Vorbelegungs-Effekt keinen Wert bauen, weil er den Index nicht
 *  kennt.
 *
 *  ⚠️ Und als Konstante, weil er an VIER Stellen als Literal stand
 *  (Regel-15-Warnung W-6) — vier Gelegenheiten, ihn unterschiedlich zu
 *  schreiben.
 */
const KIND_LOSER_WERTKEY = 'wert';
const KIND_LOSER_CODEKEY = 'wert_code';

const optionAxisKind = (label) => {
  if (!label) return null;
  const lower = label.toLowerCase();
  if (/(model|modell)/i.test(lower)) return 'model';
  if (/(size|capacity|größe|grösse|größe)/i.test(lower)) return 'size';
  if (/(color|colour|farbe)/i.test(lower)) return 'color';
  return null;
};

const findShopifyAxisLabel = (options, kind) => {
  if (!options?.length) return null;
  const match = options.find((opt) => optionAxisKind(opt.name) === kind);
  return match?.name || null;
};

/**
 * Human-readable reason for a row that could not be given a SKU.
 * The codes come from `resolveVariantSku` in utils/skuComposition.js.
 */
/**
 * Why a row has no SKU, in words an operator can act on.
 *
 * Two things this deliberately does NOT do, both from the Rule 16 gate:
 *  - it never prints a raw segment key. `segmentLabels` maps model/size/colour
 *    onto whatever the axis is actually CALLED on screen, which on this
 *    German-language store is often "Farbe" or "Größe".
 *  - it never appends "(null)". The misconfiguration reason carries no segment,
 *    and an unconditional suffix rendered that literal token to the user.
 *
 * The result is a SENTENCE, so it does not go in a Chip: MUI clamps a Chip label
 * to `white-space: nowrap` + `text-overflow: ellipsis` (the `root` and `label`
 * slot styles in `@mui/material/Chip`) and the dialog is capped at
 * maxWidth="sm", so there is no viewport where these fit. The chip stays a
 * short state token; the sentence renders beside it.
 */
const SKU_REASON_LABELS = {
  // Not a failure — the one reason code that reports a normal state. The row
  // has no SKU because it is not supposed to have one yet.
  issued_by_server: () => 'Assigned by the server on creation.',
  fixed_segment_invalid: {
    vendor:   'Vendor code missing — fix the vendor in Vendors',
    category: 'Category code missing or not 2 digits — check the category mapping',
    base:     'Base number missing — reselect the category',
  },
  axis_code_missing:  (seg) => `No SKU code is registered for the chosen ${seg} value — use "+ Add value"`,
  segment_unresolved: {
    // mode='variants': there IS a product, and its SKU is unusable.
    variants: (seg) => `This product has no 12-digit SKU, so the ${seg} digits cannot be inherited`,
    // mode='new': there is no product yet — the operator has to fill the axis.
    new: (seg) => `Pick a ${seg} value — a new SKU needs all six segments`,
  },
  resolver_misconfigured: () => 'SKU could not be built. Reload the wizard and report this if it repeats.',
};

/** Short, chip-sized state token. Never a sentence. */
const skuReasonChip = (reason) =>
  // `issued_by_server` is NOT a failure: the row has no SKU because the server
  // assigns it. A warning-coloured "no SKU" would read as a problem to fix and
  // send the operator looking for a control that does not exist.
  reason?.code === 'issued_by_server' ? 'server-assigned'
    : (reason ? 'no SKU' : 'missing SKU');

/** Warning for a genuine gap, neutral for the server-assigns case. */
const skuReasonChipColor = (reason) =>
  (reason?.code === 'issued_by_server' ? 'default' : 'warning');

/**
 * @param {object|null} reason      {code, segment} from resolveVariantSku
 * @param {object} segmentLabels    segment key -> the label shown on screen
 * @param {'new'|'variants'|'basic'} mode
 */
const skuReasonText = (reason, segmentLabels = {}, mode = 'new') => {
  if (!reason) return 'This row has no SKU.';
  const shown = segmentLabels[reason.segment] || reason.segment || '';
  const entry = SKU_REASON_LABELS[reason.code];
  if (!entry) return 'This row has no SKU.';
  if (typeof entry === 'function') return entry(shown);
  if (reason.code === 'segment_unresolved') {
    return (mode === 'variants' ? entry.variants : entry.new)(shown);
  }
  return entry[reason.segment] || 'This row has no SKU.';
};

/**
 * One aggregated notice when the form's fixed SKU segments disagree with the
 * product being extended. It REPORTS; the product's value is what gets used and
 * nothing rewrites the existing SKU.
 *
 * Rendered on Options & Variants, SKU preview and Review — the operator
 * confirms SKUs on all three, and would otherwise click past the only place
 * that says the form's category was overruled.
 */
const SkuDriftNotice = ({ rows, segmentLabels = {} }) => {
  const drift = new Map();
  (rows || []).forEach(r => (r.skuDrift || []).forEach(d => drift.set(d.segment, d)));
  if (!drift.size) return null;
  const items = [...drift.values()];
  const sentence = (d) =>
    `The ${segmentLabels[d.segment] || d.segment} code in this form (${d.form}) differs `
    + `from this product's SKU (${d.product}). The product's value is used.`;
  return (
    // role="status", not Alert's default role="alert": this is a persistent,
    // non-urgent statement that never clears for an overflow product, and an
    // assertive live region would interrupt a screen reader on all three steps.
    // Same split NoticeSlot already makes.
    <Alert severity="info" role="status" sx={{ mb: 2 }}>
      This is expected for overflow categories — the picker cannot offer them.
      {items.length === 1 ? ` ${sentence(items[0])}` : (
        <ul style={{ margin: '8px 0 0', paddingInlineStart: '1.25rem' }}>
          {items.map((d) => <li key={d.segment}>{sentence(d)}</li>)}
        </ul>
      )}
    </Alert>
  );
};

export default function UnifiedCreateWizard({
  open,
  onClose = () => {},
  onSuccess = () => {},
  mode = 'new',                 // "new" | "variants" | "basic"
  initialVendor = null,
  initialProductId = null,
  caseProductReference = null,
  defaultCategory = null, sourceContext = null, poId = null, poCurrency = null, // PR2 PO-Schnellweg: Dateiende
}) {
  /* ----------------------------- state ----------------------------- */
  // Mobile breakpoint detection for the basic-mode wizard. Wizards in this
  // app are expected to be full-screen on mobile (pattern-library §Wizard
  // Pattern Rules) so they don't compete with the surrounding TransitionSheet
  // bottom-sheet for vertical space.
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [status, setStatus] = useState('idle');     // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  // master data (for code mappings)
  const [vendors, setVendors]   = useState([]);     // includes vendor_sku
  const [catMap, setCatMap]     = useState([]);     // [{category, category_code}]
  // Kategorie-Register je Vertikale (Spec 2026-10-12 §5.5). catMap bleibt NUR
  // fuer den category_code-Lookup der Legacy-SKU-Komposition.
  const [kategorieRegister, setKategorieRegister] = useState([]);
  const [kategorieRegisterStatus, setKategorieRegisterStatus] = useState('idle'); // idle|loading|ok|error
  const [modelMap, setModelMap] = useState([]);     // [{model, model_code}]
  const [sizeMap, setSizeMap]   = useState([]);     // [{size, size_code}]
  const [colorMap, setColorMap] = useState([]);     // [{color, color_code}]
  const [productCatalog, setProductCatalog] = useState([]);
  const [productCatalogLoading, setProductCatalogLoading] = useState(false);
  const [productCatalogError, setProductCatalogError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [axisLabelOverrides, setAxisLabelOverrides] = useState({
    model: '',
    size: '',
    color: '',
  });

  // base info
  const [productName, setProductName] = useState('');
  const [vendorId, setVendorId]       = useState(initialVendor?.id || '');
  const [category, setCategory]       = useState('');
  // Vertical -> Google Shopping custom_label_1. No default on purpose; see
  // VerticalSelect's header for why guessing one is worse than blocking.
  const [verticalKey, setVerticalKey] = useState('');
  const [baseNumber, setBaseNumber]   = useState('');   // 2-digit (server also suggests)
  const [allocatorCategoryCode, setAllocatorCategoryCode] = useState(null);

  // existing product (mode=variants)
  const [productId, setProductId]             = useState(initialProductId || '');
  const [shopifyOptions, setShopifyOptions]   = useState([]);     // [{name, values:[...]}]
  const [existingVariants, setExistingVariants] = useState([]);
  // 🔴 P2 — der ausdrueckliche Haken. Standardmaessig AUS: eine 0 soll eine
  // bewusste Handlung sein, kein Durchrutschen. Ohne ihn gaebe es gar keinen
  // Weg mehr, eine absichtlich unbepreiste Variante anzulegen — und eine
  // Sperre ohne Ausweg wird umgangen statt befolgt.
  const [nullpreisGewollt, setNullpreisGewollt] = useState(false);
  // Vergibt der SERVER die SKU? Der Schalter ist backendseitig — im Frontend
  // waere er baugebunden (`REACT_APP_*`) und zur Laufzeit nicht umlegbar.
  // Deshalb wird er ERFRAGT (`GET /api/products/sku-issuance`).
  //
  // 🔴 Ohne diese Auskunft entstand die Sackgasse, die beide Gates gemessen
  // haben: der Wizard leitet Modell- und Groessenziffern aus der Eltern-SKU ab,
  // eine vergebene Nummer hat keine, und `canNext` blieb tot.
  const [schalterAn, setSchalterAn] = useState(false);
  const [serverVergibtSku, setServerVergibtSku] = useState(false);
  // Die Nutzlast, die `onSuccess` beim Schliessen bekommt (Fix 2, 2026-09-27).
  // Sie wird gebraucht, weil der Erfolg jetzt ein SICHTBARER Zustand ist und
  // nicht mehr im selben Tick endet, in dem er entsteht.
  const [successPayload, setSuccessPayload] = useState(null);

  // Single→multi colour prompt (grill decision C7): when the operator adds a
  // colour axis to a product that doesn't have one yet, the existing sole
  // variant needs an explicit colour value assigned — we ask rather than
  // guess. See `needsColourPrompt` below and the reconcile contract's
  // `updates:[{target:"sole",...}]`.
  const [soleVariantColour, setSoleVariantColour] = useState('');
  const [colourPromptOpen, setColourPromptOpen] = useState(false);
  const [colourPromptValue, setColourPromptValue] = useState('');

  // store actual selected objects for those 3 well-known types
  const [modelValues, setModelValues] = useState([]); // [{model, model_code}]
  const [sizeValues,  setSizeValues]  = useState([]); // [{size, size_code}]
  const [colorValues, setColorValues] = useState([]); // [{color, color_code}]

  // 🔴 Werte je Achsen-NAME, fuer Achsen die auf kein POG-Kind abbilden
  // (`Variante`, `Ausfuehrung`, `Travel Case` — gemessen 8 Achsen an 8 aktiven
  // Produkten, plus jede neu eingefuehrte Achse).
  //
  // Warum ein eigener Zustand und nicht modelValues/sizeValues/colorValues:
  // die sind je KIND geschluesselt. Zwei Achsen ohne Kind kollidierten dort auf
  // demselben Slot. Der Name ist der einzige Schluessel, den sie sicher
  // unterscheidet — Shopify bindet Optionen ohnehin ueber den Namen.
  //
  // 🔴 Und er wird GELESEN, nicht nur geschrieben (Regel-15-Befund N2a): die
  // erste Fassung dieses Umbaus schrieb im Setter hinein, baute `values` aber
  // weiter direkt aus den Live-Werten — der Autocomplete haette nach jeder
  // Aenderung denselben Stand gerendert und die Eingabe des Bedieners
  // verworfen.
  const [liveAxisValues, setLiveAxisValues] = useState({}); // { [achsenName]: [{...}] }

  // 🔴 Die ZUSAETZLICHE Achse — die eigentliche Anforderung (2026-09-26).
  //
  // `values` traegt NUR die NEUEN Werte. `valueForExisting` ist der Wert, den
  // die BESTEHENDEN Varianten auf der neuen Achse bekommen, und steht bewusst
  // NICHT in `values`: stuende er dort, erzeugte `cartesian` die Zeile
  // `schwarz / Standard` als NEUE Variante, waehrend `updates` dieselbe
  // Variante gerade auf `Standard` zieht. Shopify lehnt das mit
  // `INVALID_VARIANT` ab und die GANZE Mutation scheitert (live gemessen
  // 2026-09-26). Der Server kann den Bestandswert nicht raten — der Bediener
  // muss ihn sagen.
  const [extraAxis, setExtraAxis] = useState(null);
  // { name: '', values: [], valueForExisting: null }
  // “Add value” dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState(null); // 'model' | 'size' | 'color'
  const [addLabel, setAddLabel] = useState('');
  const [addCode, setAddCode] = useState('');
  const nextMappingCodes = useMemo(() => ({
    model: computeNextMappingCode(modelMap, 'model_code'),
    size: computeNextMappingCode(sizeMap, 'size_code'),
    color: computeNextMappingCode(colorMap, 'color_code'),
  }), [modelMap, sizeMap, colorMap]);

  // generated variant rows
  const [rows, setRows] = useState([]); // [{optValues, title, sku, exists, moq, cost, price}]
  const [previewing, setPreviewing] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [duplicateStatus, setDuplicateStatus] = useState('idle');

  // 🔴 Kein Opt-out-Schalter für die Anreicherung mehr (Owner-Entscheid A,
  // 2026-10-26): im Modus `new` ist sie IMMER an, auch ohne Quellen. Ein
  // Ausschalter wäre eine zweite Wahrheit gegen „immer“. Die Felder baut
  // `baueAnreicherungsFelder` (./anreicherungNutzlast.js).
  // LSM Control article create (mode='new') — default ON.
  // See docs/superpowers/plans/2026-06-16-product-create-lsm-sync.md (PR1, D1/D8).
  const [createInLsm, setCreateInLsm] = useState(true);
  const [sourceUrls, setSourceUrls] = useState('');
  const [imageUrls, setImageUrls] = useState('');
  // 🔴 Varianten-Sperre (2a): die Bild-LISTE meldet die AUSWAHL; die Nutzlast
  // liest sie statt des Freitexts. `null` = „noch nichts gemeldet" ⇒ Freitext.
  const [bildAuswahl, setBildAuswahl] = useState(null);
  const [variantenFarben, setVariantenFarben] = useState({});
  const [highlightImageUrl1, setHighlightImageUrl1] = useState('');
  const [highlightImageUrl2, setHighlightImageUrl2] = useState('');
  const [promptText, setPromptText] = useState('');
  const [promptDocUrl, setPromptDocUrl] = useState('');
  const [notifyOnEnrichment, setNotifyOnEnrichment] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [blogTopicsEnabled, setBlogTopicsEnabled] = useState(false);

  /* ----------------------------- basic-mode state ----------------------------- */
  // mode === 'basic' uses a flat dialog (no stepper) to create a product on the
  // fly — e.g. from the RMA replacement-product picker when no matching product
  // exists yet. Posts to /api/products v2 (real Shopify push, allocator-issued
  // SKU) and skips AI enrichment. It has NOT posted to /api/products/provisional
  // since 2026-05-27; that route is deprecated and creates a local draft only.
  // Because this is a real Shopify create, it needs a vertical like any other.
  const [basicForm, setBasicForm] = useState({
    productName: '',
    variantName: '',
    category: '',
    cost: '',
    currency: 'EUR',
    // No default. A guessed vertical writes a wrong custom_label_1 into the live
    // catalogue and the product is then served by no DE/AT campaign, silently.
    verticalKey: '',
  });
  // mode='basic' LSM Control toggle — default OFF (D6) to avoid orphan LSM
  // articles for ad-hoc spare adds. Separate from createInLsm (default ON for
  // new/variants). See docs/superpowers/plans/2026-06-16-product-create-lsm-sync.md.
  const [createInLsmBasic, setCreateInLsmBasic] = useState(sourceContext === 'po'); // W5: PO startet AN (Dateiende)
  // Inline error state for the vertical field — see handleBasicSubmit.
  const [basicVerticalError, setBasicVerticalError] = useState(false);
  const [basicVendor, setBasicVendor] = useState(initialVendor || null);
  const [basicSubmitting, setBasicSubmitting] = useState(false);
  const [basicError, setBasicError] = useState('');
  // Category dropdown options — populated from live taxonomy (NOT hardcoded
  // English values). Live values are German (e.g. "Zubehör und Ersatzteile").
  const [categories, setCategories] = useState([]);
  // 'loading' | 'ok' | 'error' — drives the empty-state fallback so the Select
  // doesn't sit on "Loading…" forever when the categories endpoint is down.
  const [categoriesStatus, setCategoriesStatus] = useState('loading');
  // Die Vorgabe der aufrufenden Flaeche (`defaultCategory`), GEMERKT statt
  // gesetzt. 🔴 Bis zum 2026-10-13 stand sie direkt in `basicForm.category`:
  // ohne Vertikale sind die Kinder des Selects genau `[MenuItem value=""]`,
  // MUI findet kein passendes Kind, `displaySingle` bleibt `undefined` — das
  // Feld rendert NICHTS, waehrend der Wert gesetzt ist, und er ging ueber
  // `category: basicForm.category` unsichtbar in die Anlage (Regel-16-Blocker 1a,
  // deterministisch bei JEDEM Oeffnen von SparePartCreateDialog).
  const vorgabeKategorieRef = useRef(null);

  // Fetch the category list for the CHOSEN vertical in basic mode (Spec §5.5).
  // 🔴 Ohne Vertikale wird gar nicht geladen: die Liste ist leer und der Select
  // benennt den Grund ("Pick a vertical first"). Bis zum 2026-10-12 kam sie
  // aus `/api/products/categories` und war damit vertikal-blind — ein Messer
  // liess sich mit "Kaffee-Mühlen" anlegen.
  //
  // 🔴 Und hier steht die Invariante, an EINER Stelle erzwungen statt an drei
  // nachgetragen: **`basicForm.category` ist `''` oder ein Mitglied von
  // `categories`.** Vorher leerte nur der `.then`-Zweig sie, die beiden anderen
  // Wege (kein Vertikal / Abruf gescheitert) liessen eine Kategorie der ALTEN
  // Vertikale stehen — unsichtbar, weil kein Kind sie mehr trug, und genau
  // deshalb gefaehrlich (Regel-16-Blocker 1b).
  useEffect(() => {
    if (!open || mode !== 'basic') return;
    const vertikale = basicForm.verticalKey;
    if (!vertikale) {
      setCategories([]);
      setCategoriesStatus('idle');
      setBasicForm((prev) => (prev.category ? { ...prev, category: '' } : prev));
      return;
    }
    let aktiv = true;
    setCategoriesStatus('loading');
    axios
      .get(`${API}/api/products/category-registry?vertical=${encodeURIComponent(vertikale)}`)
      .then((res) => {
        if (!aktiv) return;
        const liste = Array.isArray(res.data) ? res.data : [];
        const namen = liste.map((k) => k.category_name);
        setCategories(namen);
        setCategoriesStatus('ok');
        // Eine Kategorie der ALTEN Vertikale darf nicht stehenbleiben — sonst
        // schickt die Anlage ein Santoku mit custom_label_1 fuer Kaffee. Und
        // die gemerkte Vorgabe wird erst uebernommen, wenn die Liste sie kennt.
        setBasicForm((prev) => {
          if (prev.category) {
            return namen.includes(prev.category) ? prev : { ...prev, category: '' };
          }
          const vorgabe = vorgabeKategorieRef.current;
          return vorgabe && namen.includes(vorgabe) ? { ...prev, category: vorgabe } : prev;
        });
      })
      .catch(() => {
        if (!aktiv) return;
        setCategories([]);
        setCategoriesStatus('error');
        // Leere Liste heisst: kein Mitglied mehr. Der Wert MUSS weg.
        setBasicForm((prev) => (prev.category ? { ...prev, category: '' } : prev));
      });
    return () => { aktiv = false; };
  }, [open, mode, basicForm.verticalKey]);

  // Reset basic-mode form when wizard opens / mode changes / caller-provided
  // defaults change.
  useEffect(() => {
    if (!open || mode !== 'basic') return;
    // Die Vorgabe wird GEMERKT, nicht gesetzt — uebernommen wird sie erst,
    // wenn der Abruf oben sie in der Liste der gewaehlten Vertikale findet.
    vorgabeKategorieRef.current = defaultCategory || '';
    setBasicForm({
      productName: '',
      variantName: '',
      category: '',
      cost: '',
      currency: 'EUR',
      verticalKey: '',
    });
    setBasicVendor(initialVendor || null);
    setBasicError('');
    setBasicVerticalError(false);
    setBasicSubmitting(false);
    // 🔴 `initialVendor?.id`, NICHT das Objekt. `ReplacementProductPicker`
    // baut bei JEDER Kreditvorschau (`credit-preview`, `setResolvedVendor`)
    // ein frisches Objektliteral;
    // haengt der Effekt am Objekt, leert eine Vorschau waehrend des Tippens
    // Produktname, Kosten und Vertikale. Gemessen vom Regel-16-Gate. Vorher
    // kaum erreichbar, weil die Schaltflaeche erst nach der Hydrierung
    // erschien — seit sie frueher da ist, ist es der Normalfall.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, defaultCategory, initialVendor?.id]);

  const handleBasicChange = (field, value) => {
    setBasicForm((prev) => ({ ...prev, [field]: value }));
  };

  // Greift die Kaffee-Ersatzliste? Genau dann zeigt das Select statt der
  // Registerliste die vier Ersatznamen an. Anzeige und Riegel lesen dieselbe
  // Bedingung (Regel-16-Runde-2-Blocker 1).
  const ersatzlisteGreift =
    basicForm.verticalKey === 'brewout' && categories.length === 0 && categoriesStatus === 'error';

  // Die Werte, die das Category-Select WIRKLICH anbietet — die eine Groesse, an
  // der sich der Riegel unten messen muss. Sonst lehnt die Anlage Werte ab, die
  // das Feld selbst anbietet.
  const angeboteneKategorien = ersatzlisteGreift ? KAFFEE_ERSATZKATEGORIEN : categories;

  // Die id des gerade sichtbaren Hinweises unter dem Feld, damit der Select ihn
  // ueber `aria-describedby` benennen kann (Hausform wie `sku-block-summary`).
  const basicKategorieHilfeId = !basicForm.verticalKey
    ? 'basic-category-help-vertical'
    : (categoriesStatus === 'error' && !ersatzlisteGreift ? 'basic-category-help-error' : undefined);

  const handleBasicSubmit = async () => {
    if (basicSubmitting) return;
    // 🔴 Die Vertikale zuerst: ohne sie haelt das Category-Select gar keinen
    // waehlbaren Eintrag bereit (`!basicForm.verticalKey` unten), die Kategorie
    // ist also nicht etwa vergessen, sondern noch nicht waehlbar. Stuende der
    // Sammelriegel zuerst, hoerte der Bediener „name, cost, vendor, and category
    // are required" und nie den Grund (Regel-8-Befund 2026-10-13, Zweigbein 3).
    if (!basicForm.verticalKey) {
      // Both surfaces on purpose: the Alert is where every other basic-mode
      // validation message appears, and the inline state is what the operator
      // sees when the Alert is scrolled out of view (fullScreen on mobile).
      setBasicVerticalError(true);
      setBasicError('Pick a vertical. It drives Google Shopping custom_label_1 and has no default.');
      return;
    }
    if (!basicForm.productName || !basicForm.cost || !basicVendor || !basicForm.category) {
      setBasicError('Product name, cost, vendor, and category are required.');
      return;
    }
    // 🔴 Die Invariante, ein ZWEITES Mal — hier als benannter Fehler statt als
    // unsichtbarer Wert in der Nutzlast (`category: basicForm.category`). Der
    // Effekt oben haelt sie normalerweise bereits; dieser Riegel greift, wenn
    // die Liste nach der Wahl neu geladen wurde und die Kategorie nicht mehr
    // kennt. Regel-16-Blocker 1, Empfehlung 1.
    //
    // 🔴 Er prueft gegen `angeboteneKategorien`, NICHT gegen die Registerliste
    // allein: fuer `brewout` bei gescheitertem Abruf ist `categories` leer, das
    // Select bietet aber die vier Ersatznamen an. Gegen die rohe Liste lehnte
    // die Anlage genau die Werte ab, die das Feld selbst anbietet
    // (Regel-16-Runde-2-Blocker 1).
    if (!angeboteneKategorien.includes(basicForm.category)) {
      setBasicError(
        `The category '${basicForm.category}' does not belong to the selected vertical. Pick one from the list.`
      );
      return;
    }
    setBasicSubmitting(true);
    setBasicError('');

    // Build a minimal v2 payload. Basic-add has no real axes/variants so we use
    // a 1-axis 1-value 'Standard' shape — same trick the spare-parts ad-hoc
    // creates use elsewhere in POG.
    const pad2 = (n) => String(n).padStart(2, '0');

    // 🔴 Vergibt der SERVER die Nummer fuer DIESE Kategorie? Basic-Add fragte
    // das bisher gar nicht und komponierte bedingungslos — als einziger der
    // drei Anlagewege. Der Stepper fragt ueber `kategorieName`, das aber an
    // `category` haengt; Basic-Add hat seinen eigenen Zustand
    // (`basicForm.category`), und der Effekt deckt ihn nicht ab. Deshalb hier
    // ein eigener Aufruf statt einer Umverdrahtung des geteilten Effekts.
    //
    // Faellt WEICH aus: ist die Auskunft nicht zu holen, wird komponiert wie
    // bisher. Gleiche Richtung wie der Stepper-Effekt auf `kategorieName` —
    // der Server verwirft
    // eine mitgeschickte Nummer, wenn er selbst vergibt, ein Zuviel ist also
    // folgenlos. Ein Zuwenig waere `400 variant.sku is required`.
    const frageWerVergibt = async () => {
      // ⚠️ KEIN Kurzschluss ueber `schalterAn`. Er sparte einen GET und kostete
      // Richtigkeit: der Oeffnen-Abruf (`sku-issuance` im Init-Effekt) faellt
      // weich aus, ein einziger
      // Aussetzer haette den Schalter fuer die GANZE Dialogsitzung auf aus
      // eingefroren — und das `Promise.all` daneben setzt ihn bei jedem anderen
      // Stammdatenfehler gar nicht erst. Vom Regel-15-Gate mit eigenem
      // Diskriminator gemessen, nachdem ich die Aenderung als
      // "verhaltensgleich" ausgegeben hatte. Jede Absendung fragt frisch und
      // heilt sich damit selbst.
      try {
        const { data } = await axios.get(`${API}/api/products/sku-issuance`, {
          params: { category: basicForm.category },
        });
        // 🔴 `issues`, NICHT `opaque`. `opaque` ist der Schalter, `issues` die
        // Antwort fuer DIESE Kategorie — der Server vergibt nur bei
        // vorhandener `sku_category_mapping`-Zeile. Die Kategorieliste des
        // flachen Dialogs kommt aus `SELECT DISTINCT category_name FROM
        // shp_products` (`list_product_categories` in `backend/products_v2.py`),
        // nicht aus der Zuordnung:
        // unzugeordnete Kategorien sind waehlbar, der Fall ist erreichbar.
        // Wer hier `opaque` liest, schickt fuer sie keine Nummer und bekommt
        // `400 variant.sku is required`. Genau dieser Rueckfall ist auf der
        // Serverseite schon zweimal passiert.
        return !!data?.issues;
      } catch {
        return false;
      }
    };

    const buildPayload = async (serverVergibt) => {
      // 1. Fetch allocator (code, base) for this category — handles overflow.
      //    Entfaellt, wenn der Server vergibt: eine undurchsichtige Nummer hat
      //    keine Basisnummer, und `create_product_v2` (Step 7) setzt
      //    `product_base_number` in dieser Betriebsart ohnehin auf NULL
      //    — der Allocator-Wert waere verworfen.
      if (serverVergibt) {
        return {
          idempotency_key: makeUuid(),
          vendor_id: basicVendor.id,
          vertical_key: basicForm.verticalKey,
          product_name: basicForm.productName,
          category: basicForm.category,
          body_html: `<p>${basicForm.variantName || basicForm.productName}</p>`,
          ...herkunftUndAnreicherung(sourceContext, poId),
          skip_lsm_sync: !createInLsmBasic,
          axes: [{ name: 'Variante', values: ['Standard'] }],
          variants: [{
            // KEIN `sku`: `_validate_payload(sku_optional=True)` laesst ihn
            // weg, und der Server vergibt ihn selbst.
            option_values: ['Standard'],
            moq: 1,
            cost: String(basicForm.cost),
            price: String(basicForm.cost),
          }],
        };
      }
      const allocRes = await axios.get(`${API}/api/next-sku-components`, {
        params: { category: basicForm.category },
      });
      const code = allocRes.data?.category_code;
      const base = allocRes.data?.product_base_number;
      if (!code || base == null) {
        throw new Error(
          `SKU allocator returned an incomplete response for category '${basicForm.category}'. Try again, or pick a different category.`
        );
      }
      // 2. Compose 12-digit SKU. Basic-add has 1 dummy axis → model/size/color
      //    segments all zero-pad. vendor_sku is the 2-digit numeric prefix.
      // A3: explicit null-check first — the old String(…||'').padStart(2,'0')
      //     coercion silently produced '00' when vendor_sku was undefined,
      //     baking an invalid vendor prefix into every basic-add SKU.
      // 🔴 Der Code kommt aus der GELADENEN Lieferantenliste, nicht aus dem
      // uebergebenen Objekt. Der RMA-Einstieg saet `{id, vendor_name}` ohne
      // `vendor_sku` (Seed-Effekt aus `caseData.vendor_config` in
      // `ReplacementProductPicker`), und er kann gar
      // nicht anders: `rma_vendor_configs` fuehrt die Spalte nicht. Ohne
      // Nachschlag endete der Dialog mit "Vendor code missing", obwohl der
      // Lieferant den Code hat — beide Gates haben genau das gemessen,
      // nachdem ich die Schaltflaeche freigegeben hatte.
      // `vendors` traegt ihn (`list_vendors` in `backend/vendors.py` liefert
      // `vendor_sku`), und der Vergleich ist derselbe wie bei `currentVendor`.
      const vendorSkuRaw = vendors.find(
        (v) => String(v.id) === String(basicVendor?.id),
      )?.vendor_sku ?? basicVendor?.vendor_sku;
      if (vendorSkuRaw == null || vendorSkuRaw === '') {
        // 🔴 ZWEI Ursachen, zwei Saetze. Reisst einer der fuenf harten
        // Stammdaten-Abrufe im `Promise.all` des Init-Effekts, bleibt
        // `vendors` leer,
        // der Nachschlag greift ins Leere — und der Bediener laese sonst, der
        // Lieferant habe keinen Code, obwohl nur nichts geladen wurde. Der
        // flache Dialog rendert `status === 'error'` nicht, es gibt also
        // keinen anderen Hinweis. Vom Regel-16-Gate gemessen; `vendors` haengt
        // an `[open]`, Schliessen und Oeffnen heilt es.
        throw new Error(
          vendors.length === 0
            ? 'Vendor master data did not load, so no SKU can be composed for this product. Close this dialog and open it again — that reloads it, but clears what you typed here. If it keeps happening, reload the page.'
            // 🔴 Der Satz nennt KEINEN Handlungsort mehr, und das ist die
            // Korrektur. Die vorige Fassung schickte den Bediener nach Shopify
            // und schloss mit "then run the vendor sync" — den Sync gibt es
            // nicht: `sync/sync_vendors.py` kompilierte nie (`IndentationError`,
            // Zeile 62) und ist seit dem 2026-09-25 geloescht; einen Vendor-Sync
            // gibt es damit nicht. Ich hatte diese Tatsache in derselben
            // Sitzung im Kommentar von `backend/sku_issuance.py` gelesen und
            // die Datei trotzdem als Handlungsort ausgegeben; das Regel-16-Gate
            // hat es mit `py_compile` gemessen.
            //
            // Schreiben koennen den Code genau zwei Stellen, keine davon dem
            // Bediener zugaenglich: `PATCH /api/vendors/<id>/sku`
            // (`update_vendor_sku` in `backend/vendors.py`, ohne
            // Frontend-Aufrufer) und `create_vendor` ebendort, das ihn selbst
            // vergibt. Ein Satz, der
            // eine unmoegliche Handlung verlangt, ist schlechter als einer, der
            // ehrlich ans Melden verweist.
            : `No vendor code on record for ${basicVendor?.vendor_name || basicVendor?.id}, and POG composes this product's SKU from it. There is no screen in POG that can set it — ask an admin to add it to the vendor record, then create the product.`
        );
      }
      const vendorSku = String(vendorSkuRaw).padStart(2, '0').slice(0, 2);
      if (!/^\d{2}$/.test(vendorSku)) {
        throw new Error(
          `Vendor code '${vendorSkuRaw}' on ${basicVendor?.vendor_name} is not two digits, so no SKU can be composed from it. Vendors shows the code but cannot edit it yet — it has to be corrected on the vendor record.`
        );
      }
      const composedSku = `${vendorSku}${code}${pad2(base)}000000`;
      const idempotencyKey = makeUuid();
      return {
        idempotency_key: idempotencyKey,
        vendor_id: basicVendor.id,                  // Shopify metaobject ID
        vertical_key: basicForm.verticalKey,        // server rejects the create without it
        product_name: basicForm.productName,
        category: basicForm.category,
        body_html: `<p>${basicForm.variantName || basicForm.productName}</p>`,
        product_base_number: pad2(base),
        ...herkunftUndAnreicherung(sourceContext, poId), // C: no enrichment for ad-hoc spare adds — EXCEPT the PO (file end)
        skip_lsm_sync: !createInLsmBasic,           // PR2: default OFF (D6) — opt in per ad-hoc spare add
        axes: [{ name: 'Variante', values: ['Standard'] }],
        variants: [{
          sku: composedSku,
          option_values: ['Standard'],
          moq: 1,
          cost: String(basicForm.cost),
          price: String(basicForm.cost),
        }],
      };
    };

    // EINMAL fragen, nicht je Versuch: die Antwort haengt an Schalter und
    // Kategorie, und beide aendern sich waehrend der Schleife nicht.
    const serverVergibt = await frageWerVergibt();

    let attempts = 0;
    let lastErr = null;
    while (attempts < 3) {
      try {
        const payload = await buildPayload(serverVergibt);
        const res = await axios.post(`${API}/api/products`, payload);
        // v2 success shape: sku lives at variants[0].sku for fresh creates,
        // top-level .sku only on the idempotent-replay branch. Read both.
        const createdSku = res.data?.variants?.[0]?.sku || res.data?.sku;
        onSuccess({
          sku: createdSku, product_id: res.data?.product_id ?? null, moq: 1, // W-A (PR2): file end
          product_name: basicForm.productName,
          variant_name: basicForm.variantName || basicForm.productName,
          vendor_name: basicVendor.vendor_name,
          vendor_id: basicVendor.id,
          cost_per_item: parseFloat(basicForm.cost),
          currency: wirksameWaehrung(sourceContext, poCurrency, basicForm.currency), // R16 W3: Dateiende
        });
        onClose();
        setBasicSubmitting(false);
        return;
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        const code = err.response?.data?.error;
        // Allocator-driven retry: 409 sku_conflict means the just-computed SKU
        // was taken between allocator call and INSERT. Recompose with fresh
        // (code, base) on next iteration.
        //
        // 🔴 NICHT wiederholen, wenn der Server vergibt. Es gibt dann keine
        // Client-Nummer, die sich neu zusammensetzen liesse — der Versuch
        // wuerde nur eine ZWEITE Nummer ziehen, und weil `idempotency_key` in
        // `buildPayload` erzeugt wird, unter einem NEUEN Schluessel: aus einem
        // Konflikt entstuende ein zweites Produkt. Der Fehler gehoert dann vor
        // den Bediener, nicht in eine Schleife.
        // ⚠️ Der Zusatz `code !== 'idempotency_in_progress'` stand hier und ist
        // wieder raus: die Marke ist aus DIESEM Dialog nicht erreichbar, weil
        // `makeUuid()` in `buildPayload` je Versuch einen frischen Schluessel
        // praegt — ein frischer Schluessel kann nicht schon laufen. Vom
        // Regel-16-Gate gemessen, gegen die Warnung der ersten Regel-15-Runde,
        // die den Fall theoretisch angenommen hatte. Wer den Schluessel je vor
        // die Schleife zieht, muss die Ausnahme mit zurueckholen.
        if (!serverVergibt
            && (status === 409 || code === 'sku_conflict' || code === 'unique_violation')) {
          attempts += 1;
          continue;
        }
        break;
      }
    }
    // 🔴 Klartext statt Maschinenmarke. Der Wiederhol-Arm hat diese drei Marken
    // bisher weggefangen; seit er nur noch im Komponier-Betrieb greift, landen
    // sie im Alert — und keine von ihnen fuehrt ein `message`-Feld, der Bediener
    // laese also `sku_conflict`. Der Assistent uebersetzt an eigener Stelle
    // (`submitV2WithRetry`), der flache Dialog hatte dafuer nichts.
    // 🔴 Nur ERREICHBARE Marken. Die erste Fassung uebersetzte vier, davon drei
    // aus diesem Dialog unerreichbar: `idempotency_in_progress` und
    // `reserved_skus_mismatch` brauchen einen wiederverwendeten
    // Idempotenzschluessel (`buildPayload` praegt je Versuch einen neuen), und
    // `unique_violation` gehoert zur stillgelegten `/products/provisional`.
    // Vom Regel-16-Gate gemessen. Uebrig bleiben die drei, die der Bediener
    // wirklich sehen kann.
    //
    // ⚠️ `sku_conflict` ist im Vergabe-Betrieb sehr unwahrscheinlich, aber nicht
    // unmoeglich: die Pruefung in `create_product_v2` haengt an
    // `if not mapping_missing:`, und das trifft dort ebenso zu. Die erste
    // Fassung dieses Kommentars behauptete "nur im Komponier-Betrieb" — das
    // war zu stark, gefunden vom Regel-16-Gate.
    //
    // Der Satz raet deshalb NICHT zum Kategoriewechsel. Der Bediener saehe ihn
    // erst nach drei Anlaeufen; das ist ein Zaehler hinter dem Bestand, kein
    // Kategorieproblem — und ein Wechsel verfaelschte die Einordnung.
    const KLARTEXT = {
      sku_conflict:
        'The SKU for this product collides with one that already exists. That is not something you can fix from here — tell an admin, with the vendor and category you picked.',
      vendor_not_found:
        'This vendor is not in POG. Open Vendors and check that the vendor still exists before creating a product for it.',
      invalid_sku:
        'The SKU composed for this product is not twelve digits. That is a fault in this dialog, not something you did — tell an admin, with the vendor and category you picked.',
    };
    const marke = lastErr?.response?.data?.error;
    const msg = KLARTEXT[marke]
             || lastErr?.response?.data?.message
             || lastErr?.response?.data?.details?.join('; ')
             || marke
             || lastErr?.message
             || 'Failed to create product';
    setBasicError(msg);
    setBasicSubmitting(false);
  };

  // productSet-unification Task 7 (FIX 2): the add-to-existing/reconcile
  // flow (mode='variants', submits via reconcileVariants -> POST
  // /api/products/<pid>/variants/reconcile) is backend-enforced
  // single-axis — the route 400s with `multi_axis_unsupported` if the
  // request would leave the product with more than one live option axis.
  // Model + Size were a dead end there: the operator could fill in all
  // three axes, walk the entire wizard, and only get rejected at the final
  // Create with a jargon error (see the friendlier message in `finish`'s
  // catch block below). Restrict the axis list itself to Colour-only for
  // that flow so the pickers, the "Map Shopify option names" panel, the
  // variant matrix, and the reconcile payload can never surface Model/Size
  // in the first place. The NEW-product create flow (mode='new', posts to
  // /api/products v2) is untouched here — multi-axis is legitimate there.
  const axisDefinitions = useMemo(() => {
    // 🔴 Umbau 2026-09-26: im Varianten-Modus sind die Achsen die LEBENDEN
    // Achsen — nach NAMEN, nicht nach POG-Kind.
    //
    // Vorher lief das ueber `variantAxisKinds` (nur `model|size|color`). Eine
    // lebende Achse `Variante`/`Ausfuehrung` bildet auf keinen Kind ab und fiel
    // heraus; der Rueckfall zeigte dem Bediener eine „Color"-Achse, die es
    // nicht gibt. Gemessen: **8 Achsen an 8 aktiven Produkten**. An der
    // Timemore Sculptor 064S/064SSP (Farbe x Ausfuehrung, aktiv) liess sich
    // deshalb nicht einmal eine Farbe ergaenzen — der Rumpf nannte nur `Farbe`,
    // der Server erwartet beide.
    //
    // Und es war die Voraussetzung fuer die zweite Achse: solange der Wizard
    // eine Achse ohne POG-Kind nicht SIEHT, kann er sie weder anbieten noch
    // nach ihrer Einfuehrung wiederfinden — sie waere eine Einmal-Geste.
    const bauNachKind = (kind) => {
      const info = AXIS_INFO[kind];
      const label =
        axisLabelOverrides[kind] ||
        findShopifyAxisLabel(shopifyOptions, kind) ||
        info.defaultLabel;
      const values = kind === 'model'
        ? modelValues
        : kind === 'size'
          ? sizeValues
          : colorValues;
      const map = kind === 'model'
        ? modelMap
        : kind === 'size'
          ? sizeMap
          : colorMap;
      const setter = kind === 'model'
        ? setModelValues
        : kind === 'size'
          ? setSizeValues
          : setColorValues;
      return { ...info, kind, label, values, map, setter };
    };

    if (mode !== 'variants') return AXIS_ORDER.map(bauNachKind);

    // 🔴 E2 (Plan-Entscheidung): ZWEI Zweige, nicht einer.
    //
    // Hat das Produkt keine ECHTE Achse (nur `Title`/`Default Title` — 72 aktiv,
    // katalogweit 113), bleibt es beim heutigen Verhalten. Mit nur dem
    // Live-Zweig traege `axisDefinitions` die Achse `Title`: die Matrix waere
    // EINE Zeile, `exists` true, `newVariantRows` LEER und `canNext` fuer
    // „SKU preview" tot. Zusaetzlich bliebe `colorValues` leer, weil der
    // Vorbelegungs-Effekt keine Color-Achse findet -> `needsColourPrompt` nie
    // wahr -> der Einzel->Mehrfach-Farbpfad waere nicht mehr erreichbar.
    // Kein Produkt darf durch diesen Umbau einen Weg verlieren.
    const echteAchsen = (shopifyOptions || []).filter((o) => istEchteAchse(o?.name));
    if (!echteAchsen.length) {
      return variantAxisKinds(shopifyOptions, AXIS_ORDER).map(bauNachKind);
    }

    const basis = echteAchsen.map((o, i) => {
      const name = String(o.name);
      const kind = optionAxisKind(name);
      // 🔴 E1: bildet die Achse auf einen POG-Kind ab, bleibt ALLES wie heute —
      // dieselbe Zustandsquelle, dieselbe Zuordnungstabelle, derselbe Composer.
      // Nur so behalten die 8 Produkte ohne `sku_category_mapping` ihren
      // Composer-Pfad. Der LIVE-Name gewinnt trotzdem (er ist das Label).
      if (kind) return bauNachKind(kind);

      // Achse ohne Kind: eigener Zustand je Name, keine Zuordnungstabelle.
      // ⚠️ `map: []` ist hier richtig und nicht „keine Zuordnung": die Achse
      // traegt `kind: null`, liegt also nie in `AXIS_KINDS`, und
      // `resolveVariantSku` liest sie gar nicht als Segment.
      // 🔴 Konstanter Schluessel je Achse, NICHT `v${i}`. Er wird nur
      // INNERHALB dieser Achse gelesen (`getOptionLabel`, `cartesian`), und die
      // Zuordnung im Rumpf laeuft positionell ueber `activeAxes[i]` — der
      // Schluessel verlaesst den Client nie. Mit einem indexabhaengigen
      // Schluessel koennte der Vorbelegungs-Effekt (W2) keinen Wert bauen, weil
      // er den Index nicht kennt.
      const valueKey = KIND_LOSER_WERTKEY;
      const codeKey = KIND_LOSER_CODEKEY;
      return {
        kind: null,
        label: name,
        defaultLabel: name,
        addType: null,            // es gibt keine Zuordnungstabelle zum Ergaenzen
        valueKey,
        codeKey,
        map: [],
        values: liveAxisValues[name] || [],
        setter: (next) => setLiveAxisValues((prev) => ({ ...prev, [name]: next })),
      };
    });

    // 🔴 N4 (Regel-15-Befund): die Zusatzachse MUSS in DIESER Liste stehen.
    // Nur dann ist sie in `activeAxes`, nur dann bildet `cartesian` Zeilen auf
    // ihr, und nur dann nennt `new_variants[].option_values` sie — die
    // Zuordnung dort laeuft POSITIONELL ueber `activeAxes[i]`.
    //
    // 🔴 Und sie wird ANGEHAENGT, nicht eingefuegt: die Positionen in
    // `activeAxes` und in `optValues` muessen sich entsprechen. Wer sie vorne
    // einschiebt, verschiebt jedes Label.
    if (!extraAxis || !String(extraAxis.name || '').trim()) return basis;

    const valueKey = KIND_LOSER_WERTKEY;
    const codeKey = KIND_LOSER_CODEKEY;
    return [...basis, {
      kind: null,
      // 🔴 Eindeutiges Kennzeichen fuer den C2-Filter. Ein Namensvergleich traf
      // auch eine LEBENDE Achse gleichen Namens -- live gemessen: bei Name =
      // "Farbe" verschwand das Feld "Farbe values" aus dem Raster, waehrend der
      // Bediener den Namen tippte (Regel-16-Bedingung 2).
      istZusatzachse: true,
      label: String(extraAxis.name).trim(),
      defaultLabel: String(extraAxis.name).trim(),
      addType: null,
      valueKey,
      codeKey,
      map: [],
      values: (extraAxis.values || []).map((v) => ({ [valueKey]: String(v), [codeKey]: '' })),
      setter: (next) => setExtraAxis((p) => ({
        ...(p || {}),
        values: (next || []).map((o) => (typeof o === 'string' ? o : o[valueKey])),
      })),
    }];
  }, [
    mode,
    shopifyOptions,
    modelValues,
    sizeValues,
    colorValues,
    modelMap,
    sizeMap,
    colorMap,
    axisLabelOverrides,
    liveAxisValues,
    extraAxis,
  ]);

  // 🔴 Hat das Produkt ueberhaupt eine ECHTE Achse? Genau die Bedingung, unter
  // der `axisDefinitions` oben den Live-Zweig nimmt statt des E2-Rueckfalls.
  //
  // ⚠️ Sie steht hier draussen, weil das Bedienfeld sie BRAUCHT. Der
  // Regel-15-Befund: der E2-Rueckfall kehrt ZURUECK, bevor die Zusatzachse
  // angehaengt wird — das Panel hing aber an keiner Bedingung. Ein
  // Platzhalterprodukt bekam damit ein Formular, dessen Ergebnis die Route
  // beweisbar ablehnt: die Achse fehlt in `axes` (der Rueckfall traegt nur
  // `Color`), `extraAxisUpdates` nennt sie aber in `updates` -> `_paare` sieht
  // sie als ueberzaehlig -> `400 unknown_option_axis`. Und der Bediener kam
  // nicht einmal drumherum: ohne Matrixzeile verlangt `rows.length > 0` eine
  // Farbe, also ist der `sole`-Update immer mit dabei.
  // Die ANZAHL, nicht nur ein Ja/Nein: Shopify erlaubt drei Achsen, und der
  // Block darf auf einem dreiachsigen Produkt gar nicht erst erscheinen
  // (Regel-16-Blocker B3).
  //
  // Gemessen vom UX-Gate: **8 von 54** abgefragten aktiven Produkten tragen
  // bereits drei echte Achsen (alle `['Model','Size','Color']`). Ohne diese
  // Bedingung bote der Block dort eine vierte an, die Shopify mit
  // `OPTIONS_OVER_LIMIT` ablehnt — und der Bediener erfuehre es erst NACH dem
  // letzten Schritt. Dieselbe Datei begruendet das Muster an anderer Stelle
  // selbst: „server 400s without it — block here, not at submit".
  const anzahlEchteAchsen = useMemo(
    () => (shopifyOptions || []).filter((o) => istEchteAchse(o?.name)).length,
    [shopifyOptions],
  );
  const hatEchteAchsen = anzahlEchteAchsen > 0;

  // 🔴 Vorbelegung fuer Achsen OHNE POG-Kind (Regel-15-Warnung W2).
  //
  // Der Vorbelegungs-Effekt weiter unten kennt nur `model|size|color`. An der
  // Timemore Sculptor 064S/064SSP (Farbe × Ausfuehrung) muesste der Bediener die
  // lebenden `Ausfuehrung`-Werte aus dem Kopf tippen — und ein Tippfehler
  // erzeugt einen ZWEITEN Optionswert auf einer lebenden Achse, weil
  // `union_option_values` beide deklariert.
  //
  // Uebernommen wird, sobald es die Werte gibt. Hat der Bediener schon gewaehlt,
  // bleibt seine Wahl stehen (kein Ueberschreiben).
  useEffect(() => {
    if (mode !== 'variants' || !shopifyOptions?.length) return;
    setLiveAxisValues((prev) => {
      let geaendert = false;
      const next = { ...prev };
      for (const o of shopifyOptions) {
        const name = String(o?.name || '');
        if (!name || !istEchteAchse(name)) continue;
        if (optionAxisKind(name)) continue;        // Karten-Achsen: eigener Weg
        if (next[name]?.length) continue;          // Bediener hat schon gewaehlt
        const werte = (o.values || []).map((v) => String(v)).filter(Boolean);
        if (!werte.length) continue;
        next[name] = werte.map((v) => ({ [KIND_LOSER_WERTKEY]: v, [KIND_LOSER_CODEKEY]: '' }));
        geaendert = true;
      }
      return geaendert ? next : prev;
    });
  }, [mode, shopifyOptions]);

  // Segment key -> the word the operator actually sees. The axis labels come
  // from the live Shopify option names where they exist, so on this store they
  // are often German ("Farbe", "Größe") while the raw keys are not.
  const segmentLabels = useMemo(() => {
    const byKind = {};
    axisDefinitions.forEach((axis) => { byKind[axis.kind] = axis.label; });
    return {
      vendor: 'vendor',
      category: 'category',
      base: 'base number',
      model: byKind.model || AXIS_INFO.model.defaultLabel,
      size: byKind.size || AXIS_INFO.size.defaultLabel,
      color: byKind.color || AXIS_INFO.color.defaultLabel,
    };
  }, [axisDefinitions]);

  const shopifyOptionNames = useMemo(() => {
    const names = (shopifyOptions || [])
      .map((o) => String(o?.name || '').trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [shopifyOptions]);

  // 🔴 Die beiden Gruende der NEUEN Sperren, als Groessen — damit die Felder sie
  // NENNEN koennen.
  //
  // Regel-15-Befund der Wiedervorlage: die erste Fassung dieser Sperren sperrte
  // den Weiter-Knopf STUMM — genau die Klasse, die derselbe Delta im Block
  // darueber gerade behoben hatte. Ein gesperrter Knopf ohne Grund ist nicht
  // diagnostizierbar, auch wenn die Sperre inhaltlich richtig ist.
  //
  // ⚠️ Besonders W3: das Feld bietet seine Sperr-Optionen SELBST an
  // (`options={extraAxis?.values}`) — der Bediener oeffnet die Klappliste,
  // waehlt einen angebotenen Wert, und der Knopf ist tot.
  const achsenNameKollidiert = useMemo(() => {
    const name = String(extraAxis?.name || '').trim().toLowerCase();
    if (!name) return false;
    return shopifyOptionNames.some((n) => String(n).trim().toLowerCase() === name);
  }, [extraAxis, shopifyOptionNames]);

  const bestandWertIstNeuerWert = useMemo(() => {
    const ziel = String(extraAxis?.valueForExisting || '').trim().toLowerCase();
    if (!ziel) return false;
    return (extraAxis?.values || []).some((v) => String(v).trim().toLowerCase() === ziel);
  }, [extraAxis]);

  // True when the target product has no real live option axis yet — a bare
  // Shopify single-variant product ("Title"/"Default Title"). Mirrors the
  // backend's `_is_placeholder_axes` check on the SAME live-options read
  // this wizard already loads into `shopifyOptions`
  // (GET /shopify/products/<pid>/options).
  const isPlaceholderProduct = useMemo(() => {
    if (mode !== 'variants') return false;
    if (!shopifyOptions.length) return true;
    return (
      shopifyOptions.length === 1 &&
      PLACEHOLDER_AXIS_NAMES.has(String(shopifyOptions[0]?.name || '').trim().toLowerCase())
    );
  }, [mode, shopifyOptions]);

  // The operator is introducing a Colour axis onto a product that doesn't
  // have one yet — the carried-forward sole variant needs an explicit
  // colour value (grill decision C7) or it's left without one on the
  // newly-created axis.
  const needsColourPrompt = useMemo(
    () => mode === 'variants' && isPlaceholderProduct && colorValues.length > 0,
    [mode, isPlaceholderProduct, colorValues]
  );

  // Staleness guard (Task 7 FIX 1): once the operator empties the colour
  // selection (removes all new colours — e.g. switches to a Model axis
  // instead after a failed submit), the previously-confirmed sole-variant
  // colour answer no longer applies to anything. Clear it so a later
  // resubmit can't silently carry it into a request that's no longer about
  // introducing a Colour axis. reconcileVariants() below also re-derives
  // the sole update from live needsColourPrompt/colorValues as a second,
  // independent guard.
  useEffect(() => {
    if (colorValues.length === 0) {
      setSoleVariantColour('');
    }
  }, [colorValues]);

  // 🔴 The order here decides ten of the twelve SKU digits, so it is not
  // cosmetic. Until 2026-08-25 `generated_sku` came FIRST — and `generated_sku`
  // is a dead column: repo-wide it has two declarations (`Product` in
  // `backend/models.py`, `ShpProduct` in `sync/models.py`) and exactly one
  // reader (`get_products` in `backend/routes/products_api.py`). Nothing
  // writes it. Where it is stale
  // it is also SHORTER than 12 digits, which made the wizard refuse products
  // whose `sku` was perfectly valid; a stale but 12-digit value would have been
  // worse, composing a well-formed WRONG number that the reconcile route
  // (length-check only) passes straight to Shopify, shp_products and LSM.
  //
  // Every candidate now goes through extractSkuSegments, so only a real
  // `^[0-9]{12}$` legacy SKU can become the inheritance source, whichever field
  // it came from.
  const skuSegments = useMemo(() => {
    if (mode !== 'variants') return null;
    const candidates = [
      selectedProduct?.sku,
      selectedProduct?.generated_sku,
      ...existingVariants.map((variant) => variant?.sku),
    ];
    for (const candidate of candidates) {
      const segments = extractSkuSegments(candidate);
      if (segments) return segments;
    }
    return null;
  }, [mode, selectedProduct, existingVariants]);

  // 🔴 UEBERHOLT seit P2 (2026-08-28) — und der alte Hinweis war ab da
  // gefaehrlich, nicht nur veraltet.
  //
  // Er sagte: „in mode='variants' the modelValues/sizeValues this sets reach
  // nothing — axisDefinitions is colour-only there". Seit der Wizard die
  // LIVE-Achsen spiegelt, erreichen sie sehr wohl etwas: sie gehen als
  // Optionswerte an Shopify.
  //
  // Aus den SKU-SEGMENTEN vorzubelegen ist dann falsch. Fuer das gemeldete
  // Produkt (HAR, Size-Segment `32`) waehlt die Zuordnungstabelle `Unigroesse`,
  // waehrend die lebende Achse `102mm` traegt — `union_option_values` haenge
  // `Unigroesse` an die echte `Size`-Achse. Das Regel-16-Gate hat es am
  // Bildschirm gemessen: 29 von 105 mehrachsigen Zeilen komponierten einen
  // Titel, den es in Shopify nicht gibt.
  //
  // Vorbelegt wird deshalb aus den LIVE-Werten, wenn es sie in der
  // Zuordnungstabelle gibt. Gibt es sie nicht, bleibt das Feld LEER — der
  // Bediener waehlt. Ein plausibler falscher Wert ist schlimmer als keiner.
  useEffect(() => {
    // Live-Werte gewinnen, sobald es sie gibt.
    if (mode === 'variants' && shopifyOptions?.length) {
      const ausLive = (kind, map, values, setter) => {
        if (values.length || !map.length) return;
        const label = findShopifyAxisLabel(shopifyOptions, kind);
        if (!label) return;
        const opt = shopifyOptions.find((o) => o?.name === label);
        const treffer = prefillFromLiveValues(opt?.values, map, AXIS_INFO[kind].valueKey);
        if (treffer.length) setter(treffer);
      };
      ausLive('model', modelMap, modelValues, setModelValues);
      ausLive('size', sizeMap, sizeValues, setSizeValues);
      ausLive('color', colorMap, colorValues, setColorValues);
      return;
    }
    if (!skuSegments) return;
    if (!modelValues.length && modelMap.length && skuSegments.model) {
      const match = rowByCode(modelMap, 'model_code', skuSegments.model);
      if (match) setModelValues([match]);
    }
    if (!sizeValues.length && sizeMap.length && skuSegments.size) {
      const match = rowByCode(sizeMap, 'size_code', skuSegments.size);
      if (match) setSizeValues([match]);
    }
    if (!colorValues.length && colorMap.length && skuSegments.color) {
      const match = rowByCode(colorMap, 'color_code', skuSegments.color);
      if (match) setColorValues([match]);
    }
  // Die drei *Values-Arrays sind bewusst NICHT gelistet: der Effekt SETZT sie,
  // und sie als Abhaengigkeit zu fuehren liesse ihn sich selbst nachtriggern.
  // Er ist gegen Wiederholung geschuetzt (`if (values.length) return`), laeuft
  // also genau einmal je Produkt. `mode` und `shopifyOptions` sind dagegen echte
  // Eingaben und deshalb ergaenzt.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    shopifyOptions,
    skuSegments,
    modelMap,
    sizeMap,
    colorMap,
    modelValues.length,
    sizeValues.length,
    colorValues.length,
  ]);

  /** Die Kombinationen, die das Produkt schon hat — fuer die Vergabe-Betriebsart,
   *  in der es keine SKU zum Vergleichen gibt. Shopify baut `title` aus
   *  denselben Optionswerten mit ' / ', wie die Matrix ihre Zeilen. */
  /** Normalisiert einen Kombinationstitel fuer den Vergleich.
   *
   *  🔴 Die Zeilenwerte kommen aus den Zuordnungstabellen, die Titel aus
   *  Shopify — niemand haelt Gross-/Kleinschreibung oder die Unicode-Form
   *  zusammen. „Schwarz" gegen „schwarz" oder ein zerlegtes „ö" waere ein
   *  falsch-negativer Vergleich, und der legt eine Variante DOPPELT an. Die
   *  alte SKU war dagegen immun, weil sie den Farbcode trug, nicht die
   *  Schreibweise. */
  const titelSchluessel = (t) =>
    // 🔴 NICHT an `/` spalten. Optionswerte enthalten selbst Schraegstriche —
    // gemessen gegen prod: `Brazil/Indonesia/Yunnan/Papua New Guinea` ist EIN
    // Wert, und 45 Produkte tragen einen Wert mit `' / '` darin. Ein Spalten
    // machte daraus mehrere Segmente und traefe einen echten dreiwertigen Titel
    // FALSCH-POSITIV: die Zeile gaelte als vorhanden und wuerde stillschweigend
    // nicht angelegt.
    //
    // Verglichen wird die GANZE Zeichenkette, nur in Form und Gross-/
    // Kleinschreibung vereinheitlicht — Zeilenwerte kommen aus den
    // Zuordnungstabellen, Titel aus Shopify, und niemand haelt beides zusammen.
    String(t ?? '').normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();

  const existingTitleSet = useMemo(() => {
    const s = new Set();
    existingVariants.forEach((variant) => {
      const t = titelSchluessel(variant?.title);
      if (t) s.add(t);
    });
    return s;
  }, [existingVariants]);

  // 🔴 Der Server vergibt nur, wenn der Schalter an ist UND die Kategorie eine
  // Zuordnung hat. Ein Wizard, der nur den Schalter kennt, schickt fuer eine
  // unzugeordnete Kategorie keine Nummer und bekommt `400 variant.sku is
  // required` — gefunden vom Regel-15-Gate.
  //
  // Faellt weich aus: kann die Auskunft nicht geholt werden, wird komponiert
  // wie bisher. Das ist der sichere Ausgang, nicht der bequeme.
  const kategorieName = mode === 'variants'
    ? (selectedProduct?.category_name || category || '')
    : (category || '');

  useEffect(() => {
    if (!schalterAn || !kategorieName) { setServerVergibtSku(false); return; }
    let abgebrochen = false;
    axios
      .get(`${API}/api/products/sku-issuance`, { params: { category: kategorieName } })
      .then(({ data }) => { if (!abgebrochen) setServerVergibtSku(!!data?.issues); })
      .catch(() => { if (!abgebrochen) setServerVergibtSku(false); });
    return () => { abgebrochen = true; };
  }, [schalterAn, kategorieName]);

  // 🔴 P2 — der Preis, den eine neue Zeile von den Geschwistern erbt.
  //
  // Als `useMemo` und nicht im Effekt versteckt: die DARSTELLUNG braucht ihn
  // auch. Das Regel-16-Gate hat gemessen, dass die Vorbelegung sonst still
  // ist — im Feld steht 3189.00 und nirgends, woher. Das ist genau das
  // Gegenteil des Entscheids aus PR #1300 (nicht raten, lieber leer lassen),
  // und ohne Kennzeichnung kann der Bediener es im Assistenten nicht
  // nachpruefen: die Liste der bestehenden Varianten zeigt Titel und SKU,
  // aber keinen Preis.
  const geerbterPreis = useMemo(
    () => preisVonGeschwistern(existingVariants),
    [existingVariants],
  );

  const existingSkuSet = useMemo(() => {
    const s = new Set();
    existingVariants.forEach((variant) => {
      const normalized = normalizeSkuString(variant?.sku);
      if (normalized) s.add(normalized);
    });
    return s;
  }, [existingVariants]);

  useEffect(() => {
    if (addType) {
      setAddCode(nextMappingCodes[addType] || pad2(0));
    } else {
      setAddCode('');
    }
  }, [addType, nextMappingCodes]);

  const openAddMappingDialog = (type) => {
    setAddType(type);
    setAddLabel('');
    setAddOpen(true);
    setAddCode(nextMappingCodes[type] || pad2(0));
  };

/* ----------------------------- steps ----------------------------- */
  // Owner-Entscheid A (2026-10-26): die Anreicherung läuft bei jeder Anlage im
  // Assistenten. Der Schritt „Source URLs“ ist deshalb IMMER dabei und
  // OPTIONAL — er ist die Stelle, an der Belege nachgereicht werden können,
  // keine Pflichtstation.
  const stepsNew = ['Vendor', 'Base info', 'Source URLs', 'Options & Variants', 'SKU preview', 'Prices', 'Review'];
  const stepsVariants = ['Select product', 'Options & Variants', 'SKU preview', 'Prices', 'Review'];
  const steps = mode === 'new' ? stepsNew : stepsVariants;
  // 🔴 NUR die ANZEIGE nennt den Schritt optional. Der Schrittname ist ein
  // SCHLUESSEL: `renderStep` waehlt den Inhalt ueber
  // `label === 'Source URLs'` — ihn in `stepsNew` umzubenennen liesse den
  // Schritt leer. R16-W3, 2026-10-27.
  const stepLabel = (s) => (s === 'Source URLs' ? 'Source URLs (optional)' : s);
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    setDuplicateStatus('idle');
  }, [rows.length, activeStep]);
  /* ----------------------------- init ----------------------------- */
  useEffect(() => {
    if (!open) return;
    setStatus('idle');
    setErrorMsg('');
    Promise.all([
      axios.get(`${API}/api/vendors`),
      axios.get(`${API}/api/sku-category-mappings`),
      axios.get(`${API}/api/sku-model-mappings`),
      axios.get(`${API}/api/sku-size-mappings`),
      axios.get(`${API}/api/sku-color-mappings`),
      // 🔴 Faellt WEICH aus: ein 404 (Frontend deployt vor Backend) wuerde sonst
      // den ganzen Stapel reissen und „Failed to load master data." in ALLEN
      // Betriebsarten zeigen — auch in denen, die diese Auskunft gar nicht
      // brauchen (RMA-Ersatzprodukt, Ersatzteilanlage).
      axios.get(`${API}/api/products/sku-issuance`).catch(() => ({ data: { opaque: false } })),
    ]).then(([v, c, m, s, col, iss]) => {
      setVendors(v.data || []);
      setCatMap(c.data || []);
      setModelMap(m.data || []);
      setSizeMap(s.data || []);
      setColorMap(col.data || []);
      // Nur die grobe Auskunft. Ob fuer DIESE Kategorie vergeben wird, haengt
      // an ihrer Zuordnung und wird unten je Kategorie nachgefragt.
      setSchalterAn(!!iss.data?.opaque);
    }).catch((e) => {
      console.error(e);
      setErrorMsg('Failed to load master data.');
      setStatus('error');
    });
  }, [open]);

  // Liste neu laden, wenn die Vertikale wechselt; eine Kategorie, die nicht
  // zur neuen Vertikale gehoert, wird zurueckgesetzt — sonst schickt die Anlage
  // ein Santoku mit custom_label_1 fuer Kaffee. Dieselbe Invariante wie im
  // Basic-Zweig, hier an denselben drei Wegen: „`category` ist `''` oder ein
  // Mitglied von `kategorieRegister`" (Regel-16-Blocker 1b — vorher leerte nur
  // der `.then`-Zweig, die beiden anderen liessen den Wert stehen).
  useEffect(() => {
    if (!open) return;
    if (!verticalKey) {
      setKategorieRegister([]);
      setKategorieRegisterStatus('idle');
      setCategory('');
      return;
    }
    let aktiv = true;
    setKategorieRegisterStatus('loading');
    axios.get(`${API}/api/products/category-registry?vertical=${encodeURIComponent(verticalKey)}`)
      .then((res) => {
        if (!aktiv) return;
        const liste = Array.isArray(res.data) ? res.data : [];
        setKategorieRegister(liste);
        setKategorieRegisterStatus('ok');
        if (category && !liste.some((k) => k.category_name === category)) setCategory('');
      })
      .catch(() => {
        if (aktiv) {
          setKategorieRegister([]);
          setKategorieRegisterStatus('error');
          // Leere Liste heisst: kein Mitglied mehr. Der Wert MUSS weg.
          setCategory('');
        }
      });
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, verticalKey]);
  // category bewusst nicht als Dep: der Effekt SETZT sie, eine Dep ergaebe eine Schleife.

  useEffect(() => {
    if (mode !== 'variants' || !selectedProduct) return;
    if (selectedProduct.vendor_id) {
      setVendorId(String(selectedProduct.vendor_id));
    }
    setProductName(selectedProduct.product_name || '');
    setCategory(selectedProduct.category_name || '');
    const base = selectedProduct.product_base_number;
    if (base != null) {
      const numeric = String(base).replace(/\\D/g, '').slice(-2);
      if (numeric) setBaseNumber(pad2(numeric));
    }
  }, [mode, selectedProduct]);

  useEffect(() => {
    if (!open || mode !== 'variants') return;
    setProductCatalogLoading(true);
    setProductCatalogError('');
    axios
      .get(`${API}/api/products`, { params: { status: 'active' } })
      .then((res) => {
        setProductCatalog(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error(err);
        setProductCatalog([]);
        setProductCatalogError('Failed to load product catalog.');
      })
      .finally(() => {
        setProductCatalogLoading(false);
      });
  }, [open, mode]);

  // auto-suggest next base number + category code (with overflow allocator) for selected category
  useEffect(() => {
    if (mode !== 'new') return;
    if (!category) return;
    // Clear allocator state before refetch so a stale value never bleeds across
    // category changes (would compose a SKU into the WRONG category bucket).
    setAllocatorCategoryCode(null);
    axios.get(`${API}/api/next-sku-components`, { params: { category } })
      .then(res => {
        const { product_base_number: n, category_code: code } = res.data || {};
        if (n != null) setBaseNumber(pad2(n));
        if (code) setAllocatorCategoryCode(code);
      })
      .catch((err) => {
        // Log so the failure isn't invisible. Clear allocator state to force a
        // retry instead of silently falling back to the dropdown's primary code
        // (would mis-route overflow buckets).
        console.error('[next-sku-components] fetch failed:', err);
        setAllocatorCategoryCode(null);
      });
  }, [category, mode]);

  // load Shopify options for existing product
  const fetchProductOptions = async (pid) => {
    if (!pid) return;
    setRows([]);
    setModelValues([]);
    setSizeValues([]);
    setColorValues([]);
    // 🔴 W-1 der Regel-15-Wiedervorlage: OHNE diese zwei Zeilen wandert eine
    // gefuellte Zusatzachse in den naechsten Produktwechsel. Ablauf: an einem
    // zweiachsigen Produkt fuellen -> Back -> dreiachsiges Produkt waehlen ->
    // Next. Das Panel sagt dort „a fourth cannot be added", waehrend die
    // veraltete Achse weiter mitgeschickt wird -> `400 too_many_option_axes`
    // erst am LETZTEN Schritt. Genau die Lage, die B3 geschlossen hat.
    setExtraAxis(null);
    setLiveAxisValues({});
    setAxisLabelOverrides({ model: '', size: '', color: '' });
    setDuplicateStatus('idle');
    // Switching target product — a colour answered for the previous
    // product's sole variant must not leak onto this one.
    setSoleVariantColour('');
    setColourPromptValue('');
    setColourPromptOpen(false);
    // Zweiter Ruecksetzpunkt, und er ist der wichtigere: der Assistent bleibt
    // beim Produktwechsel offen, der Effekt oben feuert also gar nicht. Ein
    // Haken, der fuer Produkt A gesetzt wurde, darf fuer Produkt B nicht
    // weitergelten.
    setNullpreisGewollt(false);
    try {
      const { data } = await axios.get(`${API}/api/shopify/products/${pid}/options`);
      setShopifyOptions(data.options || []);
      setExistingVariants(data.variants || []);
      const catalogMatch =
        productCatalog.find(
          (entry) =>
            String(entry.product_id || entry.sku) === String(pid) ||
            String(entry.sku) === String(pid),
        );
      if (catalogMatch) {
        setSelectedProduct(catalogMatch);
      }
    } catch (e) {
      setErrorMsg('Could not load product options from Shopify.');
      setStatus('error');
    }
  };

  /* --------------------- build matrix from values ------------------ */
  // Build combinations AND compute SKUs live whenever inputs change
  useEffect(() => {
    const activeAxes = axisDefinitions.filter(axis => axis.values.length);
    if (!activeAxes.length) {
      setRows(prev => (prev.length ? [] : prev));
      return;
    }

    const labelSegments = activeAxes.map(axis => axis.values.map(val => val[axis.valueKey]));
    const combos = cartesian(labelSegments);
    const findCode = (axis, label) => {
      if (!axis || !label) return '';
      const entry = axis.map.find((item) => String(item[axis.valueKey] || '') === String(label));
      return entry ? String(entry[axis.codeKey] || '') : '';
    };

    const vendor = vendors.find(v => String(v.id) === String(vendorId));
    // RAW on purpose: padStart/pad2 would turn an absent category code into a
    // plausible '00' before isSegment ever sees it, and the resulting SKU would
    // be well-formed and wrong. Measured 2026-08-24: 36 catalogue products carry
    // a NULL category_name, i.e. exactly that case.
    const form = {
      vendor:   String(vendor?.vendor_sku || ''),
      category: String(allocatorCategoryCode || codeBy(catMap, 'category', category, 'category_code') || ''),
      base:     String(baseNumber ?? ''),
    };

    // Hoisted out of the per-row loop: only axisCodes varies between rows, and
    // keeping the mapping call outside the setRows updater keeps it off React's
    // render path.
    const resolverBase = buildResolverArgs({
      mode, activeAxes, axisCodes: {}, form, skuSegments,
    });

    setRows(prev => {
      const prevMap = new Map(prev.map(r => [r.title, r]));
      const seen = new Set();
      const nextRows = [];
      combos.forEach(vals => {
        const title = vals.join(' / ');
        if (seen.has(title)) return;
        seen.add(title);

        const old = prevMap.get(title);
        const axisCodes = activeAxes.reduce((acc, axis, idx) => {
          acc[axis.kind] = findCode(axis, vals[idx]);
          return acc;
        }, {});

        // Vergibt der Server, wird hier NICHTS mehr zusammengesetzt. Eine
        // Vorschau-Nummer waere schlimmer als keine: sie behauptete etwas ueber
        // die Zeile, das beim Anlegen nicht eintrifft.
        const { sku, reason: skuReason, drift: skuDrift } = serverVergibtSku
          ? { sku: '', reason: { code: 'issued_by_server', segment: null }, drift: [] }
          : resolveVariantSku({ ...resolverBase, axisCodes });

        // The row stays even without a SKU — it names the segment that failed
        // instead of vanishing from the matrix without explanation.
        nextRows.push({
          optValues: vals,
          title,
          moq:   old?.moq   ?? '',
          cost:  old?.cost  ?? '',
          // 🔴 Vorbelegung aus den Geschwistern. `??`, nicht `||`: eine vom
          // Bediener bewusst geleerte Zeile darf nicht wieder vollaufen.
          // `preisVonGeschwistern` gibt '' zurueck, wenn es nichts zu erben
          // gibt — nie '0.00', denn das waere eine Aussage statt einer
          // Leerstelle, und genau so ist am 2026-08-28 eine 0-Euro-Variante
          // entstanden.
          price: old?.price ?? geerbterPreis,
          sku,
          skuReason,
          skuDrift,
          // Ohne SKU kann nicht ueber sie dedupliziert werden. Shopify setzt
          // den Variantentitel aus denselben Optionswerten mit ' / ' zusammen,
          // also traegt der Titel dieselbe Aussage.
          exists: serverVergibtSku
            ? existingTitleSet.has(titelSchluessel(title))
            : (!!sku && existingSkuSet.has(sku)),
        });
      });
      return nextRows;
    });
  }, [
    serverVergibtSku,
    existingTitleSet,
    axisDefinitions,
    vendorId,
    category,
    baseNumber,
    vendors,
    catMap,
    allocatorCategoryCode,
    existingSkuSet,
    geerbterPreis,
    mode,
    skuSegments,
  ]);

  const currentVendor = vendors.find(v => String(v.id) === String(vendorId));
  const currentVendorCode = String(currentVendor?.vendor_sku || '');
  const categoryCode = useMemo(() => {
    if (!category) return '';
    const found = catMap.find((c) => String(c.category) === String(category));
    return String(found?.category_code || '');
  }, [category, catMap]);

  /* --------------------- legacy SKU preview (client) --------------- */
  const previewSkus = async () => {
    setPreviewing(true);
    setDuplicateStatus('checking');
    setErrorMsg('');

    // SKUs are already computed in rows; verify segments (esp. vendor 2-digit)
    //
    // 🔴 Nur wenn WIR die SKU bauen. Vergibt der Server sie, ist die
    // Lieferanten-Nummer fuer das Zusammensetzen ohne Bedeutung -- die Zeilen
    // tragen noch gar keine SKU. Ohne diese Bedingung stand die Pruefung VOR dem
    // Server-Zweig weiter unten und sperrte den Weiter-Knopf mit einer Meldung
    // ueber eine Nummer, die niemand braucht (gemessen am 2026-09-27 an einem
    // Datensatz ohne `vendor_id`; alle 32 echten Lieferanten tragen einen
    // 2-stelligen Code, der Bestand loest den Defekt also nicht aus).
    const vendor = vendors.find(v => String(v.id) === String(vendorId));
    const vendorSku = String(vendor?.vendor_sku || '');
    if (!serverVergibtSku && !validSegment(vendorSku, LEN_VENDOR)) {
      setPreviewing(false);
      setErrorMsg('Vendor code must be exactly 2 digits. Fix the vendor setup in Vendors.');
      setStatus('error');
      return;
    }
    // 🔴 Vergibt der Server, gibt es hier NICHTS zu pruefen — die Zeilen haben
    // noch keine Nummer. Ohne diesen Zweig setzt jede Zeile `status='error'`,
    // und `canNext` beginnt mit `if (status !== 'idle') return false`: der
    // Bediener kaeme aus dem Fehler nicht mehr heraus, ausser durch Schliessen
    // des Dialogs. Genau das ist die Sackgasse, und `canNext` allein umzustellen
    // hat sie NICHT geschlossen (Regel-16-Gate).
    const invalid = serverVergibtSku
      ? []
      : rows.filter(r => !(r.sku && r.sku.length === SKU_LEN && isDigits(r.sku)));
    if (invalid.length) {
      setPreviewing(false);
      setErrorMsg(`Invalid SKU(s): ${invalid.length} row(s) don't meet 12-digit numeric rule.`);
      setStatus('error');
      return;
    }

    // Ohne SKU gibt es nichts nachzuschlagen — die Dubletten stehen dann schon
    // aus dem Kombinationstitel fest (`existingTitleSet`).
    if (serverVergibtSku) {
      setPreviewing(false);
      setStatus('idle');
      return;
    }

    // Optional duplicate check (nice-to-have; silently skip if endpoint missing)
    try {
      const { data } = await axios.post(`${API}/api/sku/exists`, { skus: rows.map(r => r.sku) });
      const existing = new Set(data?.existing || []);
      setRows(prev => prev.map(r => ({ ...r, exists: existing.has(r.sku) })));
      const dupCount = existing.size;
      if (dupCount > 0) {
        setErrorMsg(`${dupCount} SKU(s) already exist. Adjust codes or values.`);
        setDuplicateStatus('duplicates');
      } else {
        setErrorMsg('');
        setDuplicateStatus('ok');
      }
      setStatus('idle');
    } catch {
      // Endpoint not present: proceed without duplicate flags
      setRows(prev => prev.map(r => ({ ...r, exists: false })));
      setStatus('idle');
      setDuplicateStatus('ok');
    } finally {
      setPreviewing(false);
    }
  };

  /* ------------------------- create actions ------------------------ */
  const toMoney = (v) => (v === '' || v == null ? '' : String(v));

  // productSet-unification Task 7: send INTENT, not an assembled full variant
  // set. `new_variants` carries ONLY the rows the operator is adding
  // (`newVariantRows` below already excludes anything matching an existing
  // SKU) — the backend reads the product live and carries every existing
  // variant forward itself (products_v2.py:variants_reconcile). The old
  // /variants/bulk route required the caller to resend the assembled full
  // set client-side (the B3 anti-pattern) — that surface caused the
  // DF54/DF64 data-loss incident (2026-07-15) this replaces. `updates`
  // carries the single→multi colour re-option (see `needsColourPrompt`);
  // omitted entirely for an already-coloured or multi-variant product.
  const reconcileVariants = async (pid, soleColour) => {
    const activeAxes = axisDefinitions.filter(axis => axis.values.length);
    // Staleness guard (Task 7 FIX 1): never trust the passed-in soleColour
    // blindly — only include the "sole" update when the wizard is
    // CURRENTLY prompting for a colour (needsColourPrompt) AND a colour is
    // CURRENTLY selected. Without this, a soleColour confirmed on an
    // earlier, now-abandoned attempt (e.g. operator cleared colours and
    // picked Model instead after a failed submit) could leak into a
    // resubmit that's no longer about introducing a Colour axis.
    const soleColourToSend =
      needsColourPrompt && colorValues.length > 0 ? soleColour : '';

    // 🔴 N3 (Regel-15-Befund): `existingVariants[].id` ist die NUMERISCHE
    // REST-id (`get_shopify_product_options` in `backend/product_options.py`
    // liefert sie so). `reconcile_product`
    // lehnt ein Ziel ab, das nicht in der gid-Menge der Live-Varianten liegt —
    // ausdruecklich, damit ein falsches Ziel nicht still nichts tut
    // („M1 … never silently no-op") → `ShopifyLiveReadError` → `409
    // live_read_failed`. Die Operation, fuer die dieses Vorhaben existiert,
    // waere IMMER gescheitert. Der Wizard baut die gid deshalb hier, an EINER
    // Stelle.
    const variantGid = (v) => {
      const roh = String(v?.id ?? '');
      return roh.startsWith('gid://') ? roh : `gid://shopify/ProductVariant/${roh}`;
    };

    // 🔴 Die BESTEHENDEN Varianten muessen ihren Wert auf der neuen Achse
    // ausdruecklich nennen — der Server kann ihn nicht raten, und ohne ihn
    // antwortet er `multi_axis_split_unsupported`. Ihr vollstaendiger Satz, nicht
    // nur die neue Achse: `_paare` bindet ueber Namen und erwartet JEDE Achse.
    const neuerAchsenName = String(extraAxis?.name || '').trim();
    const extraAxisUpdates = neuerAchsenName
      ? existingVariants.filter((v) => v?.id).map((v) => ({
          target: variantGid(v),
          option_values: [
            ...(v.selected_options || []).map((e) => ({ name: e.name, value: e.value })),
            { name: neuerAchsenName, value: extraAxis.valueForExisting },
          ],
        }))
      : [];

    const payload = {
      allow_zero_price: nullpreisGewollt,
      axes: activeAxes.map((axis) => ({
        name: axis.label,
        values: axis.values.map((val) => val[axis.valueKey]),
        // 🔴 N5: genau EINE, ausdruecklich gekennzeichnete neue Achse. Der
        // Server laesst nur eine zu, und nur eine gekennzeichnete — sonst
        // koennte ein Client eine LEBENDE Achse umbenennen, indem er sie
        // mitschickt.
        ...(neuerAchsenName && axis.label === neuerAchsenName ? { is_new: true } : {}),
      })),
      new_variants: newVariantRows.map((r) => ({
        // Vergibt der Server, schickt der Client gar keine Nummer mehr. Er
        // wuerde sie ohnehin verwerfen — und was nicht geschickt wird, kann
        // nicht falsch sein.
        ...(serverVergibtSku ? {} : { sku: r.sku }),
        // 🔴 P2: BENANNT, nicht als Positionsliste. Die Reihenfolge des Clients
        // und die der Live-Achsen sind zwei unabhaengige Dinge — im Katalog
        // traegt ein Produkt (Farbe, Groesse) statt (Groesse, Farbe). Ohne
        // Namen waere die Zuordnung geraten, und die Route lehnt sie
        // mehrachsig deshalb ab (`named_option_values_required`).
        //
        // 🔴 Die Zuordnung laeuft POSITIONELL ueber `activeAxes` — die
        // Zusatzachse steht dort ANGEHAENGT, damit `optValues[i]` und
        // `activeAxes[i]` zusammenpassen.
        option_values: r.optValues.map((wert, i) => ({
          name: activeAxes[i]?.label,
          value: wert,
        })),
        price: toMoney(r.price) || '0.00',
        cost: toMoney(r.cost) || '0.00',
      })),
      // 🔴 W6 (Regel-15-Befund): ZUSAMMENGEFUEHRT, nicht ersetzt.
      //
      // ⚠️ Praezisiert nach der zweiten Gate-Runde: die erste Fassung dieses
      // Kommentars sagte, ein woertliches `updates: extraAxisUpdates` haette den
      // `sole`-Update IMMER verworfen. Das ist zu stark. `extraAxisUpdates` ist
      // nur nicht-leer, wenn ein Achsenname gesetzt ist; fuer Nicht-Platzhalter
      // ist `needsColourPrompt` immer falsch, und fuer Platzhalter ist das Panel
      // seit dem B1-Fix gar nicht mehr sichtbar. Die Zusammenfuehrung ist damit
      // heute ein No-op — sie steht als Guertel neben dem Hosentraeger, nicht
      // als Reparatur. Wer sie entfernt, bricht nichts; wer sie behaelt, bricht
      // auch nichts.
      updates: [
        ...(soleColourToSend ? [{ target: 'sole', option_values: [soleColourToSend] }] : []),
        ...extraAxisUpdates,
      ],
      // Default ON in variants mode (createInLsm defaults true). See
      // 2026-06-16-product-create-lsm-sync.
      create_in_lsm: createInLsm,
    };

    const url = `${API}/api/products/${pid}/variants/reconcile`;
    const { data } = await axios.post(url, payload);
    return data;
  };

  const createNewProductAndMaybeVariants = async () => {
    // ── PO wizard v2 migration (2026-06-02) ──────────────────────────────
    // Replaced two-call legacy flow (/api/new-product + /api/products/<id>/variants/bulk)
    // with a single POST to /api/products v2. Fixes two coupled bugs:
    //   B1: variants/bulk returns 400 when activeAxes=[] (single-variant products)
    //   B2: /api/new-product created drafts; /api/search/products has active-only filter
    // v2 endpoint handles single-variant (backend synthesises Modell=Standard when axes=[])
    // and creates DRAFT (unlisted) products (status='DRAFT' default, reverted
    // 2026-07-12 — new products must not go live automatically). /api/search/products
    // now surfaces non-provisional drafts so the picker still finds them.
    // ─────────────────────────────────────────────────────────────────────

    const first = rows[0];

    // Guard: first.sku must be valid (12 digits) — ausser der Server vergibt.
    // Dann gibt es keine Nummer zu pruefen, und der Wurf haette den ANLAGE-Pfad
    // (`mode='new'`) genauso lahmgelegt wie die Sackgasse den Varianten-Pfad.
    if (!serverVergibtSku
        && !(first?.sku && first.sku.length === 12 && isDigits(first.sku))) {
      throw new Error('First variant SKU is invalid (must be 12 numeric digits).');
    }

    const activeAxes = axisDefinitions.filter(axis => axis.values.length);
    const axisIndexMap = activeAxes.reduce((acc, axis, idx) => {
      acc[axis.kind] = idx;
      return acc;
    }, {});

    const getAxisVal = (row, kind) => {
      const idx = axisIndexMap[kind];
      const arr = row?.optValues || row?.optionValues || [];
      if (typeof idx === 'number' && idx >= 0 && idx < arr.length) return String(arr[idx] ?? '');
      if (kind === 'model') return String(row.model ?? '');
      if (kind === 'size') return String(row.size ?? '');
      if (kind === 'color') return String(row.color ?? '');
      return '';
    };

    const axisOrderByKind = activeAxes.map(axis => axis.kind);

    // ── Build v2 payload ──────────────────────────────────────────────────
    const parsedSourceUrls = (sourceUrls || '').split('\n').map(s => s.trim()).filter(Boolean);
    // 🔴 2a: die NUTZLAST liest die AUSGEWAEHLTE Menge, nicht den Freitext.
    // `null` heisst „die Liste hat noch nichts gemeldet" — dann gilt der
    // Freitext wie bisher (kein stiller Verlust).
    const parsedImageUrls = Array.isArray(bildAuswahl)
      ? bildAuswahl
      : (imageUrls || '').split('\n').map(s => s.trim()).filter(Boolean);
    const trimmedPromptText = (promptText || '').trim();
    const trimmedPromptDocUrl = (promptDocUrl || '').trim();
    const trimmedHighlight1 = (highlightImageUrl1 || '').trim();
    const trimmedHighlight2 = (highlightImageUrl2 || '').trim();
    const highlightImageUrls = [trimmedHighlight1, trimmedHighlight2].filter(Boolean);
    const trimmedNotifyEmail = (notifyEmail || '').trim();

    // 🔴 Owner-Entscheid A (2026-10-26): die Anreicherung ist im Modus `new`
    // IMMER an — auch ohne Quellen. `hasEnrichment` gibt es nicht mehr; die
    // Form baut `baueAnreicherungsFelder` (./anreicherungNutzlast.js), damit
    // sie ohne sechsstufigen Dialog prüfbar ist.
    const updateOptions = {};
    if (highlightImageUrls.length) updateOptions.highlight_image_urls = highlightImageUrls;
    if (notifyOnEnrichment || trimmedNotifyEmail) {
      updateOptions.notify = true;
      if (trimmedNotifyEmail) updateOptions.notify_email = trimmedNotifyEmail;
    }
    // 🔴 Varianten-Sperre: die im Wizard gesetzte Variantenfarbe geht als
    // POG-Spiegel-Auftrag mit (`shp_products.color`, kein Shopify-Schreiben).
    if (variantenFarben && Object.keys(variantenFarben).length > 0) {
      updateOptions.varianten_farben = variantenFarben;
    }
    // NOTE: update_targets.blog_topics intentionally NOT sent — legacy /api/new-product
    // did not send this; migration keeps behavior frozen (plan decision #3).
    const anreicherungFelder = baueAnreicherungsFelder({
      sourceUrls: parsedSourceUrls,
      imageUrls: parsedImageUrls,
      promptText: trimmedPromptText,
      promptDocUrl: trimmedPromptDocUrl,
      updateOptions,
    });

    // vendor_id in mode='new' is the Shopify metaobject ID stored in vendorId state
    // (confirmed: same identity as basicVendor.id in mode='basic').
    const buildV2Payload = (currentRows, idempotencyKey) => ({
      idempotency_key: idempotencyKey,
      vendor_id: vendorId,               // Shopify metaobject GID
      vertical_key: verticalKey,         // server rejects the create without it
      product_name: productName,
      category,
      body_html: undefined,              // not collected in mode='new' wizard
      product_base_number: baseNumber || undefined,
      ...anreicherungFelder,
      skip_lsm_sync: !createInLsm,      // PR1: default ON — see 2026-06-16-product-create-lsm-sync plan (D1/D8)
      status: 'DRAFT',                  // 2026-07-12 — new products created unlisted/draft (not live); staff activate in Shopify when ready
      // Mitgeschickt, obwohl `status: 'DRAFT'` die Sperre hier ohnehin nicht
      // ausloest: waere der Status je beweglich, faellt der Haken sonst still
      // weg — und niemand merkte es, weil DRAFT ihn nie brauchte.
      allow_zero_price: nullpreisGewollt,
      axes: activeAxes.map(axis => ({
        name: axis.label,
        // axis.values are objects ({model, model_code} etc.); the backend declares
        // Shopify product-option values from these. Emit the LABEL (val[axis.valueKey])
        // so they match the variant option_values built via getAxisVal — otherwise
        // Shopify productSet rejects every optionValue with OPTION_VALUE_DOES_NOT_EXIST.
        values: axis.values.map(val => val[axis.valueKey]),
      })),
      // When activeAxes=[], backend synthesises {name:'Modell', values:['Standard']} + option_values=['Standard']
      // Rows without a resolvable SKU now survive in `rows` so the matrix can
      // explain them (2026-08-24). canNext already blocks the step, but the
      // payload must never carry one.
      // 🔴 `filter(r => r.sku)` wirft bei Server-Vergabe JEDE Zeile weg — der
      // Rumpf traege dann `variants: []` und die Route lehnte ab. Vergibt der
      // Server, entscheidet nicht die Nummer ueber die Zeile, sondern dass es
      // sie gibt.
      variants: (serverVergibtSku ? currentRows : currentRows.filter(r => r.sku)).map(r => ({
        ...(serverVergibtSku ? {} : { sku: r.sku }),
        option_values: axisOrderByKind.map(kind => getAxisVal(r, kind)),
        moq: Number(r.moq || 1),
        cost: String(toMoney(r.cost) || '0.00'),
        price: String(toMoney(r.price) || '0.00'),
      })),
    });

    // ── SKU-collision retry loop (mirrors basic-mode pattern) ──────────────
    const vendor = vendors.find(v => String(v.id) === String(vendorId));
    const MAX_SKU_COLLISION_RETRIES = 3;
    // NOTE (2026-08-24): this recomposes SKUs for rows that do NOT exist in
    // Shopify yet — it is the 409-collision retry of the NEW-product path only.
    // It must never reach the add-variant flow, which inherits instead.
    //
    // The filter is a no-op on the reachable path (canNext already requires 12
    // digits on every row). Where it does bite it DROPS the row rather than
    // producing a 6-digit SKU from an empty tail, which is what the previous
    // `? … : ''` branch did.
    // 🔴 Der SECHSTE Verbraucher von `r.sku` als Gueltigkeitspraedikat, gefunden
    // von der Wiedervorlage — nachdem ich die Lehre „alle Verbraucher suchen"
    // aufgeschrieben und selbst nicht befolgt hatte.
    //
    // Er komponiert nach einem `409 sku_collision` mit frischen Allocator-Werten
    // neu. Vergibt der Server, gibt es nichts zu rekomponieren: seine Nummern
    // sind bereits gegen den Bestand geprueft, und ein Filter auf `r.sku` liefert
    // hier eine LEERE Liste -> `variants: []` -> 400.
    const buildRowsWithComponents = (catCode2, baseNum2) => (serverVergibtSku ? rows : rows
      .filter(r => r.sku && r.sku.length === SKU_LEN)
      .map(r => {
        // Recompose each row's SKU using fresh (catCode, baseNum) from allocator.
        // Segment layout: vendor(2) + cat(2) + base(2) + tail(6).
        const tail = r.sku.slice(6);
        const vendorSegment = (vendor?.vendor_sku || '').padStart(2, '0');
        return { ...r, sku: `${vendorSegment}${catCode2}${baseNum2}${tail}` };
      }));

    let rowsForBulk = rows;

    const submitV2WithRetry = async (currentRows, attempt = 0) => {
      const payload = buildV2Payload(currentRows, makeUuid()); // fresh key per attempt
      console.debug('PO wizard v2 payload:', payload);
      try {
        const res = await axios.post(`${API}/api/products`, payload);
        return res.data;
      } catch (err) {
        const is409 = err?.response?.status === 409;
        const errCode = err?.response?.data?.error;
        const suggested = err?.response?.data?.suggested_sku_components;
        if (is409 && (errCode === 'sku_collision' || errCode === 'sku_conflict' || errCode === 'unique_violation') && attempt < MAX_SKU_COLLISION_RETRIES) {
          const newCatCode = String(suggested?.category_code || '').padStart(2, '0');
          const newBaseNum = pad2(suggested?.product_base_number);
          if (!isDigits(newCatCode) || newCatCode.length !== 2
              || !isDigits(newBaseNum) || newBaseNum.length !== 2) {
            throw err;
          }
          setAllocatorCategoryCode(newCatCode);
          setBaseNumber(newBaseNum);
          const recomposedRows = buildRowsWithComponents(newCatCode, newBaseNum);
          setRows(recomposedRows);
          rowsForBulk = recomposedRows;
          return submitV2WithRetry(recomposedRows, attempt + 1);
        }
        if (is409 && (errCode === 'sku_collision' || errCode === 'sku_conflict')) {
          const e = new Error(
            'Could not allocate a unique SKU after several attempts — please close the wizard and retry, or pick a different category.'
          );
          e.cause = err;
          throw e;
        }
        throw err;
      }
    };

    const created = await submitV2WithRetry(rowsForBulk);

    // v2 response shape: fresh create → {variants:[{sku,...},...], shopify_product_id, ...}
    // idempotent replay → {sku: <str>, product_id: <str>}
    const createdSku = created?.variants?.[0]?.sku || created?.sku;
    const shopifyId = created?.shopify_product_id || created?.product_id;

    if (!shopifyId) {
      throw new Error('Could not resolve Shopify product ID from /api/products v2 response.');
    }

    // After possible collision-retry, persisted SKU comes from rowsForBulk[0].
    const persistedFirst = rowsForBulk[0] || first;
    const localProduct = {
      id: null,
      sku: createdSku || persistedFirst.sku,
      product_name: productName,
      variant_name: persistedFirst.title || '',
      moq: Number(persistedFirst.moq || 0),
      cost_per_item: Number(persistedFirst.cost || 0),
    };

    return {
      productId: null,
      shopifyId,
      local_product: localProduct,
      shopify_response: { product: { id: shopifyId } },
      // 🔴 `n8n_triggered` aus der Antwort, NICHT behauptet. `_maybe_trigger_n8n`
      // in `backend/products_v2.py` meldet `true`, wenn der Webhook sofort
      // zugestellt wurde, und `false`, wenn `trigger_n8n_product_enrichment`
      // ihn in die Wiederholungsschleife gelegt hat (Log „queued for retry";
      // der Takt `ai_enrichment_queue_retry` holt ihn nach). Die
      // Erfolgsquittung sagt deshalb „running" nur bei `true`.
      // ⚠️ Zwei Grenzen, bewusst ohne dritten Zweig: (1) wirft der Auslöser
      // selbst, meldet die Route ebenfalls `false`, ohne dass etwas eingereiht
      // ist — ein DB-Fehler mitten in der Anlage, kein Normalfall. (2) Ohne das
      // Feld antwortet nur der Idempotenz-Replay; den erreicht dieser Weg nicht,
      // weil `submitV2WithRetry` je Versuch einen frischen Schluessel praegt.
      // R16-W1-Rest, 2026-10-27.
      n8n_triggered: created?.n8n_triggered === true,
    };
  };



  /* --------------------------- navigation -------------------------- */
  const canNext = useMemo(() => {
    if (status !== 'idle') return false;
    const s = steps[activeStep];

    if (mode === 'new') {
      if (s === 'Vendor') return !!vendorId;
      if (s === 'Base info') {
        const nameOk = !!productName?.trim();
        const catOk  = !!category?.trim();
        const baseOk = isDigits(baseNumber) && String(baseNumber).length === 2;  // 2 digits
        const vendorOk = isDigits(currentVendorCode) && currentVendorCode.length === 2; // 2 digits
        const verticalOk = !!verticalKey;   // server 400s without it — block here, not at submit
        return nameOk && catOk && baseOk && vendorOk && verticalOk;
      }
      // „Source URLs“ hat KEINE Pflichtprüfung mehr (Owner-Entscheid A,
      // 2026-10-26). Sie stand hier bis dahin und verlangte mindestens eine
      // Quelle, einen Prompt-Text oder eine Doc-URL — genau die Bedingung, die
      // A aufhebt. Sie wird GELÖSCHT und nicht auf `true` gestellt: eine
      // Zusicherung, die nichts mehr prüfen kann, ist keine (Regel 15
      // §Rückbau). Abgesichert ist der neue Zustand durch
      // `__tests__/UnifiedCreateWizard.anreicherung-immer.test.jsx`: die Probe
      // klickt den ECHTEN Dialog im Modus `new` durch diesen Schritt (ohne
      // Eingabe), sendet ab und liest den Rumpf — plus die Playwright-Probe
      // `tests/products/product-create-enrichment-always.spec.ts` für den
      // globalen Assistenten im Browser.
   } else {
      if (s === 'Select product') return !!productId;
    }

   if (s === 'Options & Variants') {
      // 🔴 Die Zusatzachse muss VOLLSTAENDIG sein, sobald sie begonnen wurde.
      //
      // Ohne Namen oder Werte waere sie eine Achse, die nichts deklariert. Ohne
      // `valueForExisting` kann der Server den Bestandswert nicht raten — er
      // lehnt mit `multi_axis_split_unsupported` ab, aber erst NACH dem
      // Absenden und mit einem Satz statt einem Feld. Der Bediener soll es hier
      // sehen, nicht dort.
      if (extraAxis) {
        const neuerName = String(extraAxis.name || '').trim();
        const nameOk = !!neuerName;
        const werteOk = (extraAxis.values || []).length > 0;
        const bestandOk = !!String(extraAxis.valueForExisting || '').trim();
        if (!nameOk || !werteOk || !bestandOk) return false;

        // 🔴 W1 (Regel-15-Befund): der neue Name darf keine LEBENDE Achse sein.
        // Das Backend faengt es laut ab (`new_axis_already_live`), aber erst
        // nach fuenf Schritten — und `is_new` haengt im Rumpf an
        // Label-Gleichheit, also truege bei Kollision die lebende Achse das
        // Kennzeichen mit. Schreibweisen-unabhaengig, wie im Backend.
        if (shopifyOptionNames.some(
          (n) => String(n).trim().toLowerCase() === neuerName.toLowerCase())) return false;

        // 🔴 W3 (Regel-15-Befund): der Bestandswert darf kein NEUER Wert sein.
        // Waere er einer, erzeugte `cartesian` die Bestandskombination als neue
        // Variante, waehrend `updates` dieselbe Variante gerade auf diesen Wert
        // zieht — genau die INVALID_VARIANT-Lage, gegen die N1 gebaut wurde.
        // `exists` faengt sie NICHT: verglichen wird gegen die Titel VOR dem
        // Update.
        const ziel = String(extraAxis.valueForExisting).trim().toLowerCase();
        if ((extraAxis.values || []).some(
          (v) => String(v).trim().toLowerCase() === ziel)) return false;
      }

      // Vergibt der Server, gibt es keine SKU zu pruefen — sonst bliebe der
      // Weiter-Knopf fuer immer tot (die Sackgasse, die beide Gates gemessen
      // haben). Gefordert ist dann nur, dass es ueberhaupt Zeilen gibt.
      if (serverVergibtSku) return rows.length > 0;
      return rows.length > 0 &&
            rows.every(r => r.sku && r.sku.length === SKU_LEN && isDigits(r.sku));
    }

    if (s === 'SKU preview') {
      if (serverVergibtSku) return rows.some(r => !r.exists);
      const validSkus = rows.filter(r => r.sku && r.sku.length === SKU_LEN && isDigits(r.sku));
      const hasNew = validSkus.some(r => !r.exists);
      return validSkus.length > 0 && hasNew;
    }

    // 🔴 Der Preis gehoert dazu. Bis 2026-08-28 verlangte dieser Schritt nur
    // MOQ und Kosten — ein leeres Preisfeld lief durch, `toMoney('')` machte
    // daraus `''` und `|| '0.00'` daraus eine echte Null. Der Bediener sah
    // keinen Hinweis, keinen Haken (der ist auf eine LESBARE 0 verriegelt) und
    // landete nach dem Absenden auf einem Fehlerschirm, der ihm sagte, er solle
    // ein Kaestchen anhaken, das gar nicht da war. Vom Regel-16-Gate gemessen.
    if (s === 'Prices') return rows.every(r => r.moq !== '' && r.cost !== '' && r.price !== '');
    return true;
  }, [
    serverVergibtSku,
    activeStep,
    steps,
    status,
    vendorId,
    productName,
    category,
    verticalKey,
    baseNumber,
    productId,
    rows,
    mode,
    currentVendorCode,
    extraAxis,
    shopifyOptionNames,
  ]);

  const next = async () => {
    setErrorMsg('');
    const s = steps[activeStep];
    if (s === 'Select product' && productId) {
      await fetchProductOptions(productId);
    }
    if (s === 'Options & Variants') {
      await previewSkus();               // compute + validate legacy SKUs, duplicate flag if possible
    }
    setActiveStep(i => i + 1);
  };

  const back = () => setActiveStep(i => Math.max(0, i - 1));

  const finish = async (soleColourOverride) => {
    setStatus('loading');
    setErrorMsg('');
    try {
      if (mode === 'variants') {
        await reconcileVariants(productId, soleColourOverride ?? soleVariantColour);
        // 🔴 `onSuccess` gehoert NICHT hierher. Es schliesst den Dialog, und weil
        // React `setStatus` und diesen Aufruf im selben Tick buendelt, hat die
        // Erfolgsflaeche danach NIE gerendert. Sie geht jetzt beim Schliessen
        // raus -- siehe `handleRequestClose`.
        setSuccessPayload({ productId });
        setStatus('success');
        return;
      }
      const created = await createNewProductAndMaybeVariants();
      setSuccessPayload(created);
      setStatus('success');
    } catch (e) {
      console.error(e);
      // Surface the backend's actual cause (message / Shopify user_errors / validation
      // details), not just the error code — otherwise failures like shopify_failed, or
      // the reconcile route's typed 4xxs (multi_axis_unsupported, invalid_sku,
      // invalid_price, new_variant_has_id, multiple_sole_targets, duplicate_sku,
      // variant_drop_guard, ...) are undiagnosable from the modal banner. d.error +
      // d.message covers every typed error products_v2.py:variants_reconcile returns.
      const d = e?.response?.data;
      // Task 7 FIX 2: multi_axis_unsupported can still surface here even with
      // the Model/Size pickers removed from this flow — e.g. the target
      // product already has one live Shopify axis (not a placeholder) and
      // the operator is adding a Colour axis on top, which would leave the
      // product with >1 axis. The backend's message is implementation-
      // phrased ("...until name-keyed option alignment lands") — show
      // operators plain language instead.
      // 🔴 Die alte Marke bedeutete „mehrachsig geht gar nicht". Seit P2 geht es —
      // die verbliebenen Faelle bedeuten etwas ANDERES, und der alte Satz waere
      // jetzt schlicht falsch.
      if (mode === 'variants' && d?.error === 'multi_axis_split_unsupported') {
        // 🔴 C1 des Regel-16-Gates (2026-09-25). Der Satz hier lautete bis heute
        // „Adding a second option to a product that already has one is not
        // supported yet — the existing variants would need a value on the new
        // option." Das war fuer EINE der Ursachen dieser Marke falsch, und dieser
        // Zweig hat sie selbst dorthin verschoben: vorher warf die fehlende
        // benannte Angabe ein blankes ValueError -> `invalid_request` (roher
        // Maschinensatz), jetzt `MissingOptionValueError` -> DIESE Marke.
        //
        // Gemessener Pfad: ein Produkt mit zwei lebenden Achsen, davon eine ohne
        // POG-Kind (`['Farbe','Variante']`). `variantAxisKinds` faellt auf
        // `['color']` zurueck, der Wizard zeigt nur den Farbwaehler und schickt
        // `{name:'Farbe'}` — der Server erwartet `Farbe` UND `Variante` ->
        // `missing: ['Variante']`. Es wird also KEINE zweite Option
        // hinzugefuegt (das Produkt hat beide schon), und der fehlende Wert
        // gehoert auf die BESTEHENDE Achse. Der alte Satz schickte den Bediener
        // auf einen Wert fuer „the new option", den es nicht gibt.
        //
        // Die Route liefert `missing` seit 2026-09-24 mit (products_v2.py), also
        // kann der Satz die beiden Ursachen unterscheiden statt beide zu raten.
        const fehlend = Array.isArray(d?.missing) ? d.missing.filter(Boolean) : [];
        setErrorMsg(
          fehlend.length
            ? `This product carries ${fehlend.length === 1 ? 'an option' : 'options'} this dialog cannot fill (${fehlend.join(', ')}). `
              + 'A variant created here would have no value for them, so nothing was written.'
            : 'One option axis was resolved, but a variant does not match it — nothing was written.',
        );
      } else if (mode === 'variants' && d?.error === 'unknown_option_axis') {
        // 🔴 C2 des Regel-16-Gates (2026-09-25). Diese Marke ist ERREICHBAR —
        // gemessener Pfad: ein Produkt mit genau EINER lebenden Achse ohne
        // POG-Kind (`'Variante'`). `variantAxisKinds` faellt auf `['color']`
        // zurueck, `findShopifyAxisLabel(...,'color')` findet nichts, das Label
        // wird `AXIS_INFO.color.defaultLabel` = `'Color'`. Der Wizard schickt
        // `{name:'Color'}` — eine Achse, die es am Produkt nicht gibt ->
        // `400 unknown_option_axis`.
        //
        // Ohne diesen Zweig fiel die Marke bis zum gemeinsamen Ausgang durch und
        // der Bediener las den ROHEN Maschinensatz. Es ist derselbe Fehler wie
        // bei `named_option_values_required`: der Dialog hat den Achsennamen
        // erfunden, nicht der Bediener.
        const bekannt = Array.isArray(d?.known) ? d.known.filter(Boolean) : [];
        setErrorMsg(
          'This dialog sent a value under an option name this product does not have'
          + (bekannt.length ? ` (it has ${bekannt.join(', ')})` : '')
          + ' — that is a bug in this dialog, not something you did.',
        );
      } else if (mode === 'variants' && d?.error === 'named_option_values_required') {
        // Sollte den Operator nie erreichen: der Wizard schickt seit P2 immer
        // benannte Werte. Wenn doch, ist es ein Fehler IM WIZARD, und der Satz
        // sagt das, statt dem Bediener etwas vorzuwerfen.
        setErrorMsg(
          // 🔴 Der Schlüssel hiess bis 2026-09-24 `live_axes`. Er zaehlt jetzt die
          // WIRKSAMEN Achsen (lebende + die neu eingefuehrte) — bei 1 lebenden
          // und 1 neuen Achse also 2. Waere der Schluessel geblieben, stuende
          // hier „several", weil der alte nie mehr gesetzt wird.
          `This product would have ${d?.effective_axes ?? 'several'} options and the request did not name `
          + 'which value belongs to which — that is a bug in this dialog, not something you did.',
        );
      } else if (mode === 'variants' && d?.error === 'multi_axis_unsupported') {
        setErrorMsg('One option axis was resolved, but a variant does not match it.');
      } else if (d?.error === 'too_many_option_axes') {
        // 🔴 ERREICHBAR seit 2026-09-26: der Wizard fuehrt jetzt eine neue
        // Achse ein (`is_new`), ein dreiachsiges Produkt kaeme damit auf vier.
        // Der Block im Wizard verhindert das schon beim Rendern
        // (`anzahlEchteAchsen >= 3`) — dieser Zweig ist die zweite Verteidigung
        // fuer alles, was nicht durch den Wizard kommt.
        //
        // ⚠️ Bis 2026-09-26 stand hier „Heute NICHT erreichbar vom Wizard: er
        // schickt nie `is_new`". Der Satz beschrieb eine Lage, die dieser PR
        // selbst aufhebt — eine Beschreibung, die ihren Gegenstand ueberlebt
        // (Regel 17 §2). Nachgezogen, nicht gestrichen.
        setErrorMsg(
          'This product already has the maximum of three options, so a fourth cannot be added — nothing was written.',
        );
      } else if (d?.error === 'zero_price_on_active_product') {
        // 🔴 Kein `mode`-Vorbehalt: die Sperre gilt auf BEIDEN Routen, beim
        // Anlegen eines Produkts genauso wie beim Hinzufuegen einer Variante.
        //
        // Die Antwort traegt `skus` — bei acht Zeilen ist „eine Variante" ohne
        // Nummer nutzlos, und der erste Entwurf warf sie weg. Der Satz nennt
        // ausserdem nicht mehr „this product is live", weil das auf dem
        // Anlagepfad schlicht nicht stimmt: dort gibt es das Produkt noch gar
        // nicht.
        const betroffen = Array.isArray(d?.skus) ? d.skus.filter(Boolean) : [];
        setErrorMsg(
          `This product is not a draft, so a variant at 0.00 would be sold for nothing${
            betroffen.length ? ` (${betroffen.join(', ')})` : ''
          } wherever it is published. Go back, enter a price, or tick the box under that row to do it on purpose.`,
        );
      }
      // 🔴 EIN gemeinsamer Ausgang fuer alle Klartext-Marken.
      //
      // Vorher trug nur der LETZTE Zweig `setStatus('error'); return;` — die
      // beiden neuen fielen durch und wurden weiter unten mit rohem
      // Maschinentext ueberschrieben. Im Browser gemessen: der Bediener las
      // `named_option_values_required — <roher Backendtext>`. Der Klartext war
      // geschrieben und unerreichbar.
      //
      // 2026-09-25: `unknown_option_axis` kam dazu. Sie ist ERREICHBAR (gemessener
      // Pfad, s. den Zweig oben) und fiel bis dahin genau in dieselbe Luecke.
      // `too_many_option_axes` ist heute nicht erreichbar, steht aber mit drin,
      // damit die Liste vollstaendig ist, sobald Task 6 die Achseneinfuehrung bringt.
      if (d?.error === 'zero_price_on_active_product'
          || d?.error === 'too_many_option_axes'
          || (mode === 'variants' && ['multi_axis_split_unsupported',
                                      'unknown_option_axis',
                                      'named_option_values_required',
                                      'multi_axis_unsupported'].includes(d?.error))) {
        setStatus('error');
        return;
      }
      // De-dupe: Shopify emits one identical user_error per option value, which would
      // otherwise render as "... ; ... ; ...". Collapse repeats for a scannable banner.
      const uniqJoin = (arr) => [...new Set((arr || []).filter(Boolean))].join('; ');
      const detail =
        d?.message ||
        (Array.isArray(d?.user_errors) && d.user_errors.length
          ? uniqJoin(d.user_errors.map(u => u?.message))
          : null) ||
        (Array.isArray(d?.details) && d.details.length ? uniqJoin(d.details) : null);
      setErrorMsg(
        [d?.error, detail].filter(Boolean).join(' — ') || e.message || 'Create failed.'
      );
      setStatus('error');
    }
  };

  // Intercepts the "Create" click in variants-mode when the target product
  // is still a placeholder (no real colour axis) and the operator is
  // introducing one: pause and ask what the EXISTING variant's colour is
  // before submitting (see `needsColourPrompt`), instead of reconciling
  // with an unset "sole" value. Otherwise submits immediately, unchanged.
  const handleCreateClick = () => {
    if (mode === 'variants' && needsColourPrompt && !soleVariantColour) {
      setColourPromptValue('');
      setColourPromptOpen(true);
      return;
    }
    finish();
  };

  /* ------------------------------ UI ------------------------------- */

  const newVariantRows = useMemo(
    // 🔴 Vergibt der Server, hat KEINE Zeile eine Nummer — die alte Bedingung
    // liefert dann eine leere Liste, und der Anlegen-Knopf ist dauerhaft
    // deaktiviert (`!newVariantRows.length`). Dieselbe Sperre wie in
    // `previewSkus`, nur an anderer Stelle.
    () => (serverVergibtSku
      ? rows.filter((r) => !r.exists)
      : rows.filter((r) => r.sku && r.sku.length === SKU_LEN && isDigits(r.sku) && !r.exists)),
    [rows, serverVergibtSku]
  );

  const skuPreviewData = useMemo(() => {
    const skus = rows.map((r) => r.sku).filter(Boolean);
    // Rows without a SKU survive since 2026-08-24, so `rows.length` alone no
    // longer means "there is something to preview". Without this a matrix of
    // only-unresolvable rows rendered a heading over an empty chip list where it
    // used to show the vendor/category/base preview.
    if (!skus.length) {
      return {
        // Vergibt der Server, ist „keine Nummer" kein Mangel — dann steht dort
        // kein `??`, das nach einem Fehler aussieht.
        mode: serverVergibtSku ? 'issued' : 'base',
        // 🔴 IMMER mitfuehren. Der Leser unten hatte genau zwei Zweige — `base`
        // oder Chips — und ein drittes `mode` fiel in den Chip-Zweig, der
        // ungeprueft `skus.slice(...)` aufruft: TypeError im Render-Pfad, kein
        // ErrorBoundary darueber, also WEISSE SEITE. Selbst eingebaut und vom
        // Gate gefunden. Ein leeres Feld ist billiger als die Annahme, dass
        // jeder kuenftige Leser den neuen Modus kennt.
        skus: [],
        vendor: currentVendorCode || (serverVergibtSku ? '—' : '??'),
        category: categoryCode || (serverVergibtSku ? '—' : '??'),
        base: baseNumber || (serverVergibtSku ? '—' : '??'),
      };
    }
    return { mode: 'variants', skus };
  }, [rows, currentVendorCode, categoryCode, baseNumber, serverVergibtSku]);

  /** How many matrix rows currently block "Next", for the summary above the actions. */
  // 🔴 Vergibt der Server, blockiert eine Zeile OHNE Nummer gar nichts — sie
  // wartet nur. Der gelbe Hinweis „4 von 4 Varianten haben noch keine SKU"
  // stuende sonst unter einem AKTIVEN Weiter-Knopf und widerspraeche ihm.
  const blockingRowCount = useMemo(
    () => (serverVergibtSku
      ? 0
      : rows.filter((r) => !(r.sku && r.sku.length === SKU_LEN && isDigits(r.sku))).length),
    [rows, serverVergibtSku],
  );

  const VendorStep = (
    <FormControl fullWidth margin="normal">
      {/* 🔴 `labelId` ist tragend, nicht Schmuck — wie am Kategorie-Select
          (`wizard-category-label`): ohne sie hat der Select KEINEN zugaenglichen Namen,
          und weder Screenreader noch `getByLabel` finden das Feld. Gemessen
          am 2026-10-27: der neue Jest-Lauf und die Playwright-Probe koennen
          den Lieferanten sonst nicht adressieren. */}
      <InputLabel id="wizard-vendor-label">Vendor</InputLabel>
      <Select
        labelId="wizard-vendor-label"
        value={vendorId}
        label="Vendor"
        onChange={(e) => setVendorId(e.target.value)}
      >
        {vendors.map(v => (
          <MenuItem key={v.id} value={v.id}>
            {v.vendor_name}{v.vendor_sku ? ` • ${v.vendor_sku}` : ''}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const vendorCodeValid = isDigits(currentVendorCode) && currentVendorCode.length === 2;
  const BaseInfoStep = (
    <Stack spacing={2}>
      <Alert
        severity={vendorCodeValid ? 'info' : 'warning'}
        icon={false}
        sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
      >
        <Chip size="small" label={`Vendor code: ${currentVendorCode || '—'}`} />
        SKU format: 2 (vendor) + 2 (category) + 2 (base) + 2 (model) + 2 (size) + 2 (color) = {SKU_LEN}.{' '}
        {vendorCodeValid
          ? 'Vendor code looks good.'
          : 'Please set a 2-digit vendor code on the vendor record before continuing.'}
      </Alert>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <TextField
            label="Product name"
            fullWidth
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <FormControl fullWidth error={kategorieRegisterStatus === 'error'}>
            {/* labelId is load-bearing, not decoration: without it the Select has
                no accessible name and neither a screen reader nor getByLabel can
                find it. */}
            {/* 🔴 `shrink` (E-W6): `displayEmpty` zeigt AUCH bei leerem Wert
                Text, MUI leitet das Schrumpfen aber aus `value !== ''` ab. */}
            <InputLabel id="wizard-category-label" shrink>Category</InputLabel>
            <Select
              labelId="wizard-category-label"
              value={category || ''}
              label="Category"
              displayEmpty
              // 🔴 Wie in Basic-Add EXPLIZIT verdrahtet (Regel-16-Runde-2-W1):
              // MUI 7.3.5 reicht die Helper-id nicht selbst an den Select weiter.
              aria-describedby={kategorieRegisterStatus === 'error' ? 'wizard-category-help-error' : undefined}
              onChange={(e) => setCategory(e.target.value)}
              renderValue={(value) =>
                value
                  ? value
                  : <Typography color="text.secondary">Select category</Typography>
              }
            >
              <MenuItem disabled value="">
                Select category
              </MenuItem>
              {/* 🔴 Die Kategorieliste folgt der Vertikale (Spec 2026-10-12 §5.5).
                  Vorher stand hier `catMap` — die Kaffee-Zuordnung aus
                  sku_category_mapping — in JEDEM Dialog, auch fuer Peak Blades. */}
              {/* 🔴 ALLE gesperrten Platzhalter tragen `value=""`, wie die
                  Fassung in Basic-Add (Regel-16-W2). `__vertical`/`__error`
                  funktionierten (der Eintrag ist `disabled`), waren aber eine
                  zweite Schreibweise fuer denselben Hinweis im selben Zug.
                  Englisch wie die uebrige Oberflaeche (Regel-16-W6). */}
              {!verticalKey && (
                <MenuItem disabled value="">Pick a vertical first</MenuItem>
              )}
              {kategorieRegisterStatus === 'loading' && kategorieRegister.length === 0 && (
                <MenuItem disabled value="">Loading…</MenuItem>
              )}
              {kategorieRegisterStatus === 'error' && (
                <MenuItem disabled value="">Categories could not be loaded</MenuItem>
              )}
              {kategorieRegister.map((k) => {
                const code = codeBy(catMap, 'category', k.category_name, 'category_code');
                return (
                  <MenuItem key={k.category_name} value={k.category_name}>
                    {k.category_name}{code ? ` (${code})` : ''}
                  </MenuItem>
                );
              })}
            </Select>
            {/* 🔴 Der Fehler stand hier bis zum 2026-10-13 NUR als gesperrter
                Menueeintrag — wer das Menue nicht oeffnete, sah am Feld nichts
                vom gescheiterten Register-Abruf (Regel-16-Runde-2-W4). Sichtbar
                wie in Basic-Add, und ueber `aria-describedby` am Select
                angebunden. */}
            {kategorieRegisterStatus === 'error' && (
              <FormHelperText id="wizard-category-help-error">
                Categories could not be loaded.
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        {/* Widths are DECLARED now (2026-10-06, Grid-v7 conversion): `size`
            on all four children of this row, 7 + 5 | 5 + 4 — the row breaks
            into two lines from md up ON PURPOSE, exactly the split proposed in
            docs/backlog-auto-repair/2026-08-02-wizard-grid-v2-conversion.md.
            Until then the siblings carried MUI-6 `item xs/md` props, which
            MUI 7 ignores: every child sized to its CONTENT and the row fit by
            10.6px at 900px, depending on the helper text below. A single
            `size` on ONE child was honoured while the others were not, which
            forced this child to 5/12 and wrapped the row — that is why an
            earlier fix removed it. With all four converted, that trap is gone.

            What still holds: MUI never ellipsises the selected vertical's
            label here. A long `product_verticals` label grows this cell's
            CONTENT, but the cell itself now keeps its 5/12 width, so the row
            no longer reflows — the text wraps inside the field instead.
            Today's labels are "Brewout" (7) and "Peak Blades" (11). The helper
            text may change freely (guard: frontend/src/gridV7.waechter.test.js). */}
        <Grid size={{ xs: 12, md: 5 }}>
          <VerticalSelect
            value={verticalKey}
            onChange={setVerticalKey}
            helperText="Sets Google Shopping custom_label_1."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Base number"
            fullWidth
            value={baseNumber}
            onChange={(e) => setBaseNumber(e.target.value)}
            helperText="Auto-filled; must remain 2 digits"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2, readOnly: true }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* 🔴 Kein Opt-out mehr (Owner-Entscheid A, 2026-10-26): die Anreicherung
          läuft bei jeder Anlage im Assistenten. Der frühere Haken
          „Create entire product with AI enrichment“ ist gefallen — ein
          Ausschalter wäre eine zweite Wahrheit gegen „immer“. */}
      {/* role="status", nicht Alerts Vorgabe role="alert": das ist eine
          DAUERINFORMATION beim Betreten des Schritts, keine Unterbrechung.
          Dasselbe Gegenmuster mit Begruendung wie in `SkuDriftNotice` (R16-C2). */}
      <Alert severity="info" role="status">
        After creation, an AI workflow enriches this product: SEO content, descriptions, FAQ and taxonomy. It runs for every product created here — sources are not required.
      </Alert>
    </Stack>
  );

  const SourceUrlsStep = (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Source URLs for AI enrichment (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Optional — without sources the AI researches the product by its name and vendor. Add URLs to product pages, reviews, or specifications if you have them.
        </Typography>
        <TextField
          multiline
          rows={6}
          placeholder={"https://vendor-website.com/product-page\nhttps://example.com/review\nhttps://specs-page.com/details"}
          value={sourceUrls}
          onChange={(e) => setSourceUrls(e.target.value)}
          fullWidth
          helperText="One URL per line."
        />
        {sourceUrls.trim() && (
          <Alert severity="success" icon={false} sx={{ mt: 1 }}>
            {(() => {
              const n = sourceUrls.split('\n').filter(u => u.trim()).length;
              return `${n} source URL${n === 1 ? '' : 's'} provided`;
            })()}
          </Alert>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Additional prompt context (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Add any notes or structured information that should be used by the AI. This text will be injected directly into the prompt.
        </Typography>
        <TextField
          multiline
          rows={4}
          placeholder={"Key specs, vendor notes, or internal copy to include…\n\nExample: Official product name, warranty, included accessories"}
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          fullWidth
          helperText="Optional — leave empty and the AI works from the product name and vendor."
        />
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Google Doc URL (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Provide a Google Doc link with additional product details. The workflow will read the full document using connected credentials.
        </Typography>
        <TextField
          placeholder="https://docs.google.com/document/d/…"
          value={promptDocUrl}
          onChange={(e) => setPromptDocUrl(e.target.value)}
          fullWidth
          helperText="Optional: must be accessible by the n8n Google Docs credentials"
        />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Product Image URLs (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Enter direct URLs to product images that should be uploaded to Shopify. One URL per line. Use high-quality images (JPG, PNG, WebP).
        </Typography>
        <TextField
          multiline
          rows={4}
          placeholder={"https://example.com/product-image-1.jpg\nhttps://example.com/product-image-2.png\nhttps://cdn.vendor.com/product-hero.webp"}
          value={imageUrls}
          onChange={(e) => {
            setImageUrls(e.target.value);
            // Eine geaenderte Liste macht die alte AUSWAHL ungueltig: bis die
            // Liste neu meldet, gilt der Freitext (nie eine veraltete Auswahl).
            setBildAuswahl(null);
            setVariantenFarben({});
          }}
          fullWidth
          helperText="Leave empty to skip image upload"
        />
        <BildKandidatenListe
          urls={imageUrls.split('\n').map(u => u.trim()).filter(Boolean)}
          varianten={(rows || [])
            .filter(r => r?.sku)
            .map(r => ({
              sku: r.sku,
              titel: r.title || '',
              // Der Wizard nutzt seine Zeilen (Plan §5.4, `row.color`); die
              // Farbachse ist die Angebotsfarbe dieses neuen Produkts.
              farbe: String(r.color ?? '') || null,
              farbquelle: String(r.color ?? '') ? 'color' : null,
            }))}
          onAuswahlChange={setBildAuswahl}
          onFarbenChange={setVariantenFarben}
        />
        {imageUrls.trim() && (
          <Alert severity="info" icon={false} sx={{ mt: 1 }}>
            {imageUrls.split('\n').filter(u => u.trim()).length} image URL(s) will be uploaded
          </Alert>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Highlight Image URLs (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Provide up to two image URLs for highlight metaobjects. Leave empty to skip.
        </Typography>
        <Stack spacing={2}>
          <TextField
            placeholder="https://example.com/highlight-1.jpg"
            value={highlightImageUrl1}
            onChange={(e) => setHighlightImageUrl1(e.target.value)}
            fullWidth
          />
          <TextField
            placeholder="https://example.com/highlight-2.jpg"
            value={highlightImageUrl2}
            onChange={(e) => setHighlightImageUrl2(e.target.value)}
            fullWidth
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Notification (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Get an email when the AI workflow finishes or fails.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={notifyOnEnrichment}
              onChange={(e) => setNotifyOnEnrichment(e.target.checked)}
            />
          }
          label="Email me when the AI workflow finishes or fails"
        />
        {(notifyOnEnrichment || notifyEmail.trim()) && (
          <TextField
            placeholder="name@company.com"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            fullWidth
            helperText="Leave empty to use the default admin email."
          />
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Blog topic ideas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Generate 2–3 SEO blog topic seeds for this product. Topics are saved as drafts for review before publishing.
          The AI uses the source registry (curated trusted sources) to ground its suggestions.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={blogTopicsEnabled}
              onChange={(e) => setBlogTopicsEnabled(e.target.checked)}
            />
          }
          label="Generate blog topic ideas for this product"
        />
        {/* Rule 16 UX review #1: PO wizard v2 migration strips update_targets.blog_topics
            from the payload (plan decision #3 — legacy /api/new-product never sent it).
            Surface the limitation so the checkbox isn't a silent no-op. */}
        <FormHelperText sx={{ mt: 0.5 }}>
          Currently not wired into the new-product create flow. Blog topics can be triggered separately from the product detail page after creation.
        </FormHelperText>
      </Box>
    </Stack>
  );

  const SelectProductStep = (
    <Stack spacing={2}>
      <Autocomplete
        options={productCatalog}
        getOptionLabel={(option) => (
          option?.product_name
            ? `${option.product_name} (${option.sku || option.product_id || option.id})`
            : option.sku || option.product_id || option.id || ''
        )}
        value={selectedProduct}
        loading={productCatalogLoading}
        onChange={(_, value) => {
          setSelectedProduct(value || null);
          const shopifyId = value?.product_id || '';
          setProductId(shopifyId ? String(shopifyId) : '');
          if (shopifyId) {
            fetchProductOptions(String(shopifyId));
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select existing product"
            placeholder="Search by name or SKU"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {productCatalogLoading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField
            label="Shopify Product ID (numeric)"
            fullWidth
            value={productId}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              setProductId(val);
              setSelectedProduct(null);
            }}
            placeholder="e.g. 83472638123"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Button variant="outlined" fullWidth onClick={() => fetchProductOptions(productId)} disabled={!productId}>
            Load options
          </Button>
        </Grid>
      </Grid>
      {productCatalogError && (
        <Typography color="error" variant="caption">
          {productCatalogError}
        </Typography>
      )}
      {!!shopifyOptions.length && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Current Shopify options
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {shopifyOptions.map((o, idx) => (
              <Chip key={idx} label={`${o.name} (${(o.values || []).length})`} />
            ))}
          </Stack>
          {!!existingVariants.length && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Existing variants: {existingVariants.length}
            </Typography>
          )}
        </Paper>
      )}
    </Stack>
  );

  const OptionEditorStep = (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {mode === 'variants'
          ? 'Pick the values you want on each option this product has. Existing combinations are skipped. Use “Add value” to register new mappings.'
          : 'Option names (up to 3) & values (from DB mappings). Use “Add value” to register new mappings.'}
      </Typography>

      {!!shopifyOptionNames.length && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Map Shopify option names
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Select the Shopify option name for each axis. Leave blank to use the default guess.
          </Typography>
          <Grid container spacing={2}>
            {/* Task 7 FIX 2: iterate axisDefinitions (not AXIS_ORDER) so this
                mapping panel respects the same Colour-only restriction as
                the reconcile flow — no point offering a Model/Size name
                mapping when those axes can't be populated in this flow. */}
            {axisDefinitions.filter((a) => a.kind).map((axis) => {
              const kind = axis.kind;
              return (
                <Grid size={{ xs: 12, sm: axisDefinitions.length > 1 ? 4 : 12 }} key={`map-${axis.label}`}>
                  <FormControl fullWidth>
                    <InputLabel>{axis.defaultLabel} option</InputLabel>
                    <Select
                      value={axisLabelOverrides[kind] || ''}
                      label={`${axis.defaultLabel} option`}
                      onChange={(e) => {
                        const val = e.target.value;
                        setAxisLabelOverrides((prev) => ({
                          ...prev,
                          [kind]: val,
                        }));
                      }}
                    >
                      <MenuItem value="">Use default/guess</MenuItem>
                      {shopifyOptionNames.map((name) => (
                        <MenuItem key={`${kind}-${name}`} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mb: 1 }}>
        {axisDefinitions.map((axis, idx) => (
          <Grid size={{ xs: 12, sm: axisDefinitions.length > 1 ? 4 : 12 }} key={axis.label}>
            <Typography variant="caption" color="text.secondary">
              Option {idx + 1}
            </Typography>
            <Typography variant="subtitle1">{axis.label}</Typography>
          </Grid>
        ))}
      </Grid>

      {!!existingVariants.length && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2">Existing variants</Typography>
          <Typography variant="caption" color="text.secondary">
            These variants are read-only. Use the controls below to add new rows.
          </Typography>
          <Stack sx={{ mt: 1 }} spacing={1}>
            {existingVariants.map((variant) => (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" key={variant.id || variant.variant_id || variant.sku}>
                <Typography variant="body2">
                  {(variant.title || variant.variant_title || variant.sku || '').trim() || 'Untitled variant'}
                </Typography>
                <Chip label={`SKU ${variant.sku || '—'}`} size="small" variant="outlined" />
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2}>
        {axisDefinitions.filter((a) => (
          // 🔴 C2 (Regel-16-Bedingung): die Zusatzachse hat ihr eigenes Feld im
          // Block unten ("New values"). Hier stuende sie ein ZWEITES Mal, unter
          // anderem Label, auf derselben Zustandsquelle — zwei Bedienelemente
          // fuer einen Wert, ohne Querverweis.
          //
          // ⚠️ Und zwar ueber das KENNZEICHEN, nicht ueber den Namen: ein
          // Namensvergleich blendete auch eine LEBENDE Achse aus, sobald der
          // Bediener ihren Namen tippte. Er verlor dann genau das Feld, mit dem
          // er die Kollision haette vergleichen koennen. Dazu war der Vergleich
          // case-SENSITIV, waehrend die Sperre case-insensitiv ist — derselbe
          // Zustand ergab zwei verschiedene Bilder.
          !a.istZusatzachse
        )).map((axis) => (
          <Grid size={{ xs: 12, sm: axisDefinitions.length > 1 ? 4 : 12 }} key={axis.label}>
            <Autocomplete
              multiple
              // 🔴 `freeSolo` NUR wo es keine Zuordnungstabelle gibt (Achse ohne
              // POG-Kind). Dort hat der Picker keine `options`, und ohne
              // `freeSolo` koennte der Bediener einen Wert nur ENTFERNEN, nie
              // hinzufuegen — die Achse waere sichtbar und trotzdem unbedienbar.
              // Genau das braucht die zweite Achse: sie ist immer kind-los.
              //
              // ⚠️ NICHT fuer die Karten-Achsen: dort ist die Zuordnungstabelle
              // POGs Register, und ein frei getippter Wert traegt keinen Code.
              // Die Grenze ist `axis.kind === null`, nicht „leere options".
              freeSolo={axis.kind === null}
              options={axis.map}
              getOptionLabel={(option) => (
                typeof option === 'string' ? option : String(option[axis.valueKey] || '')
              )}
              value={axis.values}
              // `freeSolo` liefert getippte Eintraege als nackte Zeichenkette —
              // die muss in die Objektform, sonst liest die Matrix
              // `val[axis.valueKey]` aus einem String und zeigt „undefined".
              onChange={(_, val) => axis.setter(
                (val || []).map((v) => (
                  typeof v === 'string'
                    ? { [axis.valueKey]: v, [axis.codeKey]: '' }
                    : v
                )),
              )}
              renderInput={(params) => (
                <TextField {...params} label={`${axis.label} values`}
                  // 🔴 Regel-16-Warnung: eine kind-lose LEBENDE Achse hat keine
                  // `options`, der Bediener liest sonst nur MUI's „No options".
                  // Die Affordanz gibt es im Block unten schon — hier fehlte sie.
                  placeholder={axis.kind === null ? 'Type a value, press Enter' : undefined} />
              )}
            />
            <Box sx={{ mt: 1 }}>
              {/* 🔴 Nur wo es eine Zuordnungstabelle gibt. `openAddMappingDialog(null)`
                  kehrt sofort zurueck — der Knopf waere tot, und ein toter Knopf
                  ist schlimmer als keiner. */}
              {axis.addType ? (
                <Button size="small" onClick={() => openAddMappingDialog(axis.addType)}>
                  + Add value
                </Button>
              ) : null}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* 🔴 Die eigentliche Anforderung (2026-09-26): eine ZWEITE Achse
          einfuehren. Nur im Varianten-Modus — beim Anlegen eines Produkts
          bestimmt der Bediener die Achsen ohnehin frei.
          🔴 UND nur wenn das Produkt eine ECHTE Achse hat (`hatEchteAchsen`).
          Beim E2-Rueckfall (nur `Title`/`Default Title`) traegt
          `axisDefinitions` die Farb-Achse, die Zusatzachse wuerde dort NICHT
          angehaengt — das Panel boete ein Formular an, dessen Ergebnis die
          Route mit `400 unknown_option_axis` ablehnt (Regel-15-Blocker). */}
      {mode === 'variants' && hatEchteAchsen && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Add another option (optional)</Typography>
          {/* 🔴 E1 (Plan-Entscheidung, gemessen 2026-09-26): ohne
              `sku_category_mapping` vergibt der Server die SKU nicht, und eine
              Achse ohne POG-Kind traegt KEIN SKU-Segment (`AXIS_KINDS =
              model|size|color` in `utils/skuComposition.js`). Die neue Variante
              erbte dann die SKU ihres Geschwisters, `exists` waere true und die
              Zeile fiele STILL aus `newVariantRows` — der Bediener saehe sie
              verschwinden, ohne Grund. Ein toter Knopf mit genanntem Grund ist
              besser als eine stille Zeile.
              Betrifft 3 der 25 Zielprodukte (Kategorie ohne Zuordnung).

              🔴 B3 (Regel-16-Blocker): bei DREI echten Achsen gar nicht erst
              anbieten. Shopify erlaubt drei; die vierte antwortet
              `OPTIONS_OVER_LIMIT`. Gemessen vom UX-Gate: 8 von 54 abgefragten
              aktiven Produkten tragen bereits drei (alle Model/Size/Color).
              Dieselbe Datei begruendet das Muster selbst: „server 400s without
              it — block here, not at submit". */}
          {anzahlEchteAchsen >= 3 ? (
            <Typography variant="caption" color="text.secondary">
              This product already carries the maximum of three options, so a fourth cannot be
              added — nothing was changed.
            </Typography>
          ) : !serverVergibtSku ? (
            // 🔴 C1 (Regel-16-Bedingung): der Satz nennt EINEN von VIER
            // Gruenden. `serverVergibtSku` ist auch false, wenn der Schalter
            // aus ist, die Kategorie leer ist, oder der GET gescheitert ist
            // (alle drei im Effekt auf `kategorieName`).
            // Die Handlungsanweisung „Add the mapping" waere
            // in drei dieser vier Faelle ein Irrweg. Deshalb ursachenneutral.
            <Typography variant="caption" color="text.secondary">
              A new option cannot get its own number for this product right now — nothing was
              changed.
            </Typography>
          ) : (
          <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Introduce ONE new option on this product. Below are the NEW values. Say separately
            which value the <strong>existing</strong> variants get on it — the server cannot
            guess that, and without it nothing is written.
          </Typography>
          {/* 🔴 BLOCKER 1 des Regel-16-Gates, im Browser gemessen: unter MUI 7 ist
                  `xs`/`sm` wirkungslos, jedes Grid-Kind wird so breit wie sein INHALT.
                  Gemessen an einem echten Produkt: Container 818px, Kinder 176/57/87px
                  — 466px (57 %) der Zeile leer, und zwei der drei Labels abgeschnitten
                  ("Ne…" statt "New values", "Value fo…" statt "Value for existing
                  variants"), auch im fokussierten Zustand.
                  Deshalb FLEX statt Grid: `flex: '1 1 0'` am Kind plus
                  `minWidth: theme.spacing(28)` (224px) gibt jedem eine
                  Mindestbreite, die es nicht aus seinem Inhalt ableitet.
                  ⚠️ Beides noetig — `flex-basis: 0` allein laesst die Kinder
                  schrumpfen, `minWidth` allein verhindert das Schrumpfen nicht. ⚠️ NICHT ein
                  einzelnes Kind auf `size={{}}` umstellen — der Backlog-Eintrag
                  2026-08-02 warnt, dass das Mischen beider APIs in einer Zeile
                  schlechter ist als jede fuer sich. */}
              <Box sx={{ display: 'flex', gap: (t) => t.spacing(2), flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 0', minWidth: (t) => t.spacing(28) }}>
              {/* 🔴 B1 (Regel-16-Blocker): der Weiter-Knopf sperrt aus vier
                  Gruenden, die NUR die neue Achse betreffen — und die einzige
                  Begruendungsflaeche des Wizards (`sku-block-summary`) haengt an
                  `blockingRowCount > 0`, das bei `serverVergibtSku` fest 0 ist.
                  Also genau dann unerreichbar, wenn diese Felder rendern.
                  Deshalb traegt jedes Feld seinen Grund selbst. */}
              <TextField
                fullWidth
                label="New option name"
                error={!!extraAxis && (!String(extraAxis.name || '').trim() || achsenNameKollidiert)}
                helperText={!!extraAxis
                  ? (!String(extraAxis.name || '').trim()
                      ? 'Required for a new option.'
                      : achsenNameKollidiert
                        ? 'This is already an option on this product.'
                        : ' ')
                  : ' '}
                value={extraAxis?.name || ''}
                onChange={(e) => setExtraAxis((p) => ({
                  name: e.target.value,
                  values: p?.values || [],
                  valueForExisting: p?.valueForExisting ?? '',
                }))}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: (t) => t.spacing(28) }}>
              <Autocomplete
                multiple
                freeSolo
                // 🔴 Bedingung 1 der Regel-16-Wiedervorlage: das Feld behielt
                // den getippten Text sichtbar und committete ihn nicht — B2s
                // Mechanismus, ein Feld weiter. Die Zeile trug damit ZWEI
                // Regeln: das Nachbarfeld committet beim Verlassen, dieses
                // nicht. `autoSelect` gleicht sie an.
                autoSelect
                options={[]}
                value={extraAxis?.values || []}
                onChange={(_, val) => setExtraAxis((p) => ({
                  name: p?.name || '',
                  values: (val || []).map((v) => (typeof v === 'string' ? v : String(v))),
                  valueForExisting: p?.valueForExisting ?? '',
                }))}
                renderInput={(params) => (
                  <TextField {...params} label="New values"
                             placeholder="Type a value, press Enter"
                             error={!!extraAxis && !(extraAxis.values || []).length}
                             helperText={!!extraAxis && !(extraAxis.values || []).length
                               ? 'At least one new value is required.' : ' '} />
                )}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: (t) => t.spacing(28) }}>
              <Autocomplete
                freeSolo
                // 🔴 B2 (Regel-16-Blocker): OHNE `autoSelect` committet ein
                // freeSolo-Feld seinen Wert nicht beim Verlassen — MUI 7.3.5
                // setzt `autoSelect = false` und `clearOnBlur = !freeSolo`. Der
                // Text bleibt sichtbar stehen, `valueForExisting` bleibt `''`,
                // und der Weiter-Knopf ist tot. Das Feld BEHAUPTET dann, gefuellt
                // zu sein — schlimmer als ein sichtbar leeres.
                autoSelect
                options={extraAxis?.values || []}
                value={extraAxis?.valueForExisting || ''}
                onChange={(_, val) => setExtraAxis((p) => ({
                  name: p?.name || '',
                  values: p?.values || [],
                  valueForExisting: typeof val === 'string' ? val : String(val || ''),
                }))}
                renderInput={(params) => (
                  <TextField {...params} label="Value for existing variants"
                             // Produktneutral: der Wizard bedient den ganzen
                             // Katalog, alle anderen Platzhalter sind generisch.
                             placeholder="e.g. the one they have today"
                             error={!!extraAxis && (!String(extraAxis.valueForExisting || '').trim()
                               || bestandWertIstNeuerWert)}
                             helperText={!!extraAxis
                               ? (!String(extraAxis.valueForExisting || '').trim()
                                   ? 'Required — the server cannot guess it.'
                                   : bestandWertIstNeuerWert
                                     ? 'Must differ from the new values above.'
                                     : ' ')
                               : ' '} />
                )}
              />
            </Box>
          </Box>
          {!!extraAxis && (
            <Box sx={{ mt: 1 }}>
              {/* Verwerfen ist keine Primaeraktion — dateiweit sonst `color="error"`. */}
              <Button size="small" color="error" onClick={() => setExtraAxis(null)}>
                Discard new option
              </Button>
            </Box>
          )}
          </>
          )}
        </Paper>
      )}


      <Divider sx={{ my: 2 }} />

      <SkuDriftNotice rows={rows} segmentLabels={segmentLabels} />

      <Typography variant="subtitle2" gutterBottom>Variant matrix</Typography>
      {rows.length === 0 ? (
        <Typography color="text.secondary">Add some option values to see combinations…</Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            {rows.map((r, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Typography>{r.title}</Typography>
                {!r.sku && (
                  <>
                    {/* Filled, like the four other warning chips in this file.
                        `variant="outlined"` was tried as a contrast fix and is
                        not one: MUI picks white contrastText for warning.main
                        (getContrastText, threshold 3, and #ed6c02 vs white is
                        3.11) — and contrast is symmetric, so orange-on-white is
                        the same 3.11:1. Both fail AA for normal text; that is a
                        pre-existing, file-wide gap across all five warning chips
                        and not something this change fixes for one of them. The
                        NEW information lives in the caption below, which is
                        text.secondary on white and passes comfortably. */}
                    <Chip
                      label={skuReasonChip(r.skuReason)}
                      color={skuReasonChipColor(r.skuReason)}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {skuReasonText(r.skuReason, segmentLabels, mode)}
                    </Typography>
                  </>
                )}
                {!!r.sku && r.sku.length !== SKU_LEN && (
                  <Chip label="invalid length" color="warning" size="small" />
                )}
                {!!r.sku && r.sku.length === SKU_LEN && (
                  <Chip label="sku ready" size="small" color="success" />
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );

  const SkuPreviewStep = (
    <Box>
      <SkuDriftNotice rows={rows} segmentLabels={segmentLabels} />

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
        <Button
          variant="outlined"
          onClick={previewSkus}
          disabled={previewing}
          color={duplicateStatus === 'duplicates' ? 'warning' : 'primary'}
        >
          {previewing ? 'Checking…' : 'Check duplicates'}
        </Button>
        {duplicateStatus === 'ok' && (
          <Chip label="No duplicates" color="success" size="small" />
        )}
        {duplicateStatus === 'duplicates' && (
          <Chip label="Duplicates detected" color="warning" size="small" />
        )}
        {!!rows.length && (
          <Typography variant="caption" color="text.secondary">
            {serverVergibtSku
              ? `${rows.length} variant${rows.length === 1 ? '' : 's'} — numbers assigned on create`
              : `${rows.filter(r => r.sku).length}/${rows.length} generated`}
          </Typography>
        )}
      </Stack>

      {rows.length === 0 ? (
        <Typography color="text.secondary">No variants to preview.</Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Variant</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Typography>{r.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontFamily="monospace">{r.sku || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {/* 🔴 Bei Server-Vergabe hat KEINE Zeile eine Nummer, also
                        gewann dieser Zweig immer — und die Dubletten-Markierung
                        darunter war auf dem Schritt, der sie zeigen soll,
                        unerreichbar. Eine bereits vorhandene Kombination geht
                        vor: sie ist die Aussage, die den Bediener angeht. */}
                    {r.exists ? (
                      <Chip label="Duplicate" color="default" size="small" />
                    ) : !r.sku ? (
                      <Stack spacing={0.5}>
                        <Chip
                          label={skuReasonChip(r.skuReason)}
                          color={skuReasonChipColor(r.skuReason)}
                          size="small"
                          sx={{ alignSelf: 'flex-start' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {skuReasonText(r.skuReason, segmentLabels, mode)}
                        </Typography>
                      </Stack>
                    ) : r.sku.length !== SKU_LEN || !isDigits(r.sku) ? (
                      <Chip label="Invalid" color="warning" size="small" />
                    ) : r.exists ? (
                      <Chip label="Duplicate" color="error" size="small" />
                    ) : (
                      <Chip label="Ready" color="success" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );

  const PricesStep = (
    <Box>
      {rows.length === 0 ? (
        <Typography color="text.secondary">No variants to price.</Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {rows.map((r, idx) => (
              // Fragment, weil unter der Zeile noch der Ausweg-Haken haengen
              // kann — zwei Geschwister brauchen eine Klammer, und der
              // Schluessel gehoert dann an sie.
              <React.Fragment key={idx}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'minmax(200px, 1fr) 140px 160px 160px',
                  },
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography noWrap>{r.title}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {r.sku || (serverVergibtSku ? 'assigned on create' : '—')}
                  </Typography>
                </Box>
                <TextField
                  label="MOQ"
                  type="number"
                  fullWidth
                  value={r.moq}
                  onChange={(e) =>
                    setRows((old) =>
                      old.map((x, i) => (i === idx ? { ...x, moq: e.target.value } : x))
                    )
                  }
                  required
                />
                <TextField
                  label="Cost / item"
                  type="number"
                  fullWidth
                  value={r.cost}
                  onChange={(e) =>
                    setRows((old) =>
                      old.map((x, i) => (i === idx ? { ...x, cost: e.target.value } : x))
                    )
                  }
                  required
                />
                <TextField
                  label="Price (incl. tax)"
                  type="number"
                  fullWidth
                  required
                  value={r.price}
                  onChange={(e) =>
                    setRows((old) =>
                      old.map((x, i) => (i === idx ? { ...x, price: e.target.value } : x))
                    )
                  }
                  // Fehlerton, nicht neutraler Hilfstext: dieser Hinweis
                  // SPERRT das Absenden. Das Gate hat gemessen, dass er als
                  // 10,5-px-Graustufe daherkam und `aria-invalid="false"`
                  // meldete — eine Sperre, die aussieht wie eine Fussnote.
                  error={Number.parseFloat(r.price) === 0}
                  helperText={
                    // Nur bei einer LESBAREN Null. Ein unlesbarer Wert ist
                    // weder > 0 noch '' — der erste Entwurf zeigte ihm diesen
                    // Satz und log damit: das Backend antwortet dort
                    // `invalid_price`, nicht die 0-Euro-Sperre.
                    Number.parseFloat(r.price) === 0
                      ? 'A live product will not accept 0.00 unless you tick the box below.'
                      : (geerbterPreis && r.price === geerbterPreis
                          ? 'Taken from the existing variants (the highest one).'
                          : undefined)
                  }
                />
              </Box>
              {/* 🔴 Der Ausweg steht INLINE unter der betroffenen Zeile.
                  Vorher stand er einmal unter der ganzen Liste — das Gate hat
                  gemessen: bei acht Zeilen lag er bei y=773 in einem
                  Rollfenster, das bei y=718 endet, und mobil hinter der
                  Aktionsleiste. Ein Ausweg ausserhalb des Sichtfelds ist
                  keiner.

                  Er steuert EIN Kennzeichen fuer die ganze Anfrage — deshalb
                  sagt das Etikett das auch, statt Einzahl zu suggerieren.

                  E9 (2026-10-07): `FormControlLabel` ist kein `Button` — der
                  Theme-Boden greift hier nicht, der geteilte Tippziel-Boden
                  traegt (`TOUCH_TARGET_COARSE`). */}
              {Number.parseFloat(r.price) === 0 && (
                <FormControlLabel
                  sx={{ mt: -1, mb: 1, ...TOUCH_TARGET_COARSE }}
                  control={
                    <Checkbox
                      checked={nullpreisGewollt}
                      onChange={(e) => setNullpreisGewollt(e.target.checked)}
                    />
                  }
                  label="Every price of 0.00 in this list is deliberate — create them anyway"
                />
              )}
              </React.Fragment>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );

  const ReviewStep = (
    <Box>
      <SkuDriftNotice rows={rows} segmentLabels={segmentLabels} />

      {mode === 'new' && (
        // 2026-07-12: v2 create now defaults to DRAFT — products are created
        // unlisted (invisible to customers) and must be activated in Shopify
        // admin when ready.
        <Alert severity="info" sx={{ mb: 2 }}>
          Products are created as <strong>Draft</strong> in Shopify — unlisted and invisible to customers until you activate them in Shopify admin. They still appear in the in-app product picker immediately.
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Summary</Typography>
        <Grid container spacing={1}>
          {mode === 'new' ? (
            <>
              <Grid size={{ xs: 12, sm: 6 }}><strong>Vendor:</strong> {vendors.find(v => String(v.id) === String(vendorId))?.vendor_name || '—'}</Grid>
              <Grid size={{ xs: 12, sm: 6 }}><strong>Product name:</strong> {productName || '—'}</Grid>
              <Grid size={{ xs: 12, sm: 6 }}><strong>Category:</strong> {category || '—'}</Grid>
              <Grid size={{ xs: 12, sm: 6 }}><strong>Base #:</strong> {baseNumber || '—'}</Grid>
            </>
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6 }}><strong>Product ID:</strong> {productId || '—'}</Grid>
              <Grid size={{ xs: 12, sm: 6 }}><strong>Existing variants:</strong> {existingVariants.length}</Grid>
            </>
          )}
          <Grid size={12}><strong>Options:</strong> {axisDefinitions.map(axis => axis.label).join(' / ') || '—'}</Grid>
          <Grid size={12}><strong>New variants:</strong> {rows.length}</Grid>
        </Grid>
      </Paper>

      {(mode === 'new' || mode === 'variants') && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Warehouse</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={createInLsm}
                onChange={(e) => setCreateInLsm(e.target.checked)}
              />
            }
            label={mode === 'variants' ? 'Create variants in LSM Control' : 'Create product in LSM Control'}
          />
          <FormHelperText sx={{ mt: 0.5 }}>
            {mode === 'variants'
              ? 'Also create each new variant as an article in LSM Control (warehouse). On by default — untick to skip.'
              : 'Also create this product as an article in LSM Control (warehouse). On by default — untick to skip.'}
          </FormHelperText>
        </Paper>
      )}

      {/* Immer sichtbar im Modus `new` — die Anreicherung ist keine Wahl mehr
          (Owner-Entscheid A, 2026-10-26). Ohne Quellen sagt der Kasten das
          ausdrücklich, statt „0 URL(s)“ zu zeigen.
          🔴 `bgcolor: 'info.lighter'` loeste NIE auf: MUI kennt an `info` nur
          main/light/dark/contrastText, das POG-Theme ergaenzt v2.info.soft.
          `v2Tone` liefert die passende Fassung JE MODUS (hell/dunkel) — R16-W2. */}
      {mode === 'new' && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: (t) => v2Tone(t, 'info').soft }}>
          <Typography variant="subtitle2" gutterBottom>AI Enrichment — always on</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            After creation, an AI workflow will generate SEO content, descriptions, FAQ, and taxonomy classification.
          </Typography>
          <Typography variant="body2">
            <strong>Source URLs:</strong> {(() => {
              const n = sourceUrls.split('\n').filter(u => u.trim()).length;
              return n > 0 ? `${n} URL${n === 1 ? '' : 's'}` : 'none';
            })()}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {sourceUrls.split('\n').filter(u => u.trim()).slice(0, 3).map((url, idx) => (
              <Typography key={idx} variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {url}
              </Typography>
            ))}
            {sourceUrls.split('\n').filter(u => u.trim()).length > 3 && (
              <Typography variant="caption" color="text.secondary">
                + {sourceUrls.split('\n').filter(u => u.trim()).length - 3} more…
              </Typography>
            )}
          </Stack>
          {promptText.trim() && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Prompt notes:</strong> {promptText.trim().slice(0, 160)}{promptText.trim().length > 160 ? '…' : ''}
            </Typography>
          )}
          {promptDocUrl.trim() && (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Google Doc:</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {promptDocUrl.trim()}
              </Typography>
            </>
          )}
          {imageUrls.trim() && (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Product images:</strong>
              </Typography>
              <Stack spacing={0.5} sx={{ ml: 2 }}>
                {imageUrls.split('\n').filter(u => u.trim()).slice(0, 3).map((url, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {url.trim()}
                  </Typography>
                ))}
                {imageUrls.split('\n').filter(u => u.trim()).length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    + {imageUrls.split('\n').filter(u => u.trim()).length - 3} more…
                  </Typography>
                )}
              </Stack>
            </>
          )}
          {(highlightImageUrl1.trim() || highlightImageUrl2.trim()) && (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Highlight images:</strong>
              </Typography>
              <Stack spacing={0.5} sx={{ ml: 2 }}>
                {[highlightImageUrl1, highlightImageUrl2]
                  .map((url) => url.trim())
                  .filter(Boolean)
                  .map((url, i) => (
                    <Typography key={i} variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {url}
                    </Typography>
                  ))}
              </Stack>
            </>
          )}
          {(notifyOnEnrichment || notifyEmail.trim()) && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Notification:</strong> {notifyEmail.trim() || 'Default admin email'}
            </Typography>
          )}
          {blogTopicsEnabled && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Blog topics:</strong> Will generate 2–3 SEO topic seeds for review.
            </Typography>
          )}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Variants</Typography>
        {rows.length === 0 ? (
          <Typography color="text.secondary">No rows.</Typography>
        ) : (
          <Grid container spacing={1}>
            {rows.map((r, idx) => (
              <Grid size={12} key={idx}>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 12, sm: 4 }}><Typography>{r.title}</Typography></Grid>
                  <Grid size={{ xs: 12, sm: 4 }}><Typography variant="body2">{r.sku || (serverVergibtSku ? 'assigned on create' : '—')}</Typography></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><Typography variant="body2">MOQ: {r.moq || '—'}</Typography></Grid>
                  <Grid size={{ xs: 6, sm: 3 }}><Typography variant="body2">Cost: {r.cost || '—'}</Typography></Grid>
                  <Grid size={{ xs: 4, sm: 2 }}><Typography variant="body2">Price: {r.price || '—'}</Typography></Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );

  const renderStep = () => {
    const label = steps[activeStep];
    if (label === 'Vendor') return VendorStep;
    if (label === 'Base info') return BaseInfoStep;
    if (label === 'Source URLs') return SourceUrlsStep;
    if (label === 'Select product') return SelectProductStep;
    if (label === 'Options & Variants') return OptionEditorStep;
    if (label === 'SKU preview') return SkuPreviewStep;
    if (label === 'Prices') return PricesStep;
    if (label === 'Review') return ReviewStep;
    return null;
  };

  // reset when opened / mode changes
  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setStatus('idle');
    setErrorMsg('');
    setProductName('');
    setCategory('');
    setVerticalKey('');
    setBaseNumber('');
    setAllocatorCategoryCode(null);
    setSourceUrls('');
    setImageUrls('');
    setBlogTopicsEnabled(false);
    if (mode === 'new') {
      setVendorId(initialVendor?.id || '');
    } else {
      setProductId(initialProductId || '');
      setShopifyOptions([]);
      setExistingVariants([]);
      setProductCatalog([]);
      setProductCatalogError('');
      setProductCatalogLoading(false);
      setSelectedProduct(null);
    }
    setModelValues([]);
    setSizeValues([]);
    setColorValues([]);
    setExtraAxis(null);
    setLiveAxisValues({});
    setRows([]);
    setDuplicateStatus('idle');
    setSoleVariantColour('');
    setColourPromptValue('');
    setColourPromptOpen(false);
    // 🔴 Der Ausweg-Haken MUSS mit zurueck. Er ist nur sichtbar, solange eine
    // Zeile bei 0 steht — bliebe er gesetzt, ginge `allow_zero_price: true`
    // bei jedem weiteren Absenden mit, fuer ein anderes Produkt, ohne dass
    // irgendwo etwas angehakt zu sehen waere. Ein unsichtbarer Zustand, der
    // eine Sicherung abschaltet.
    setNullpreisGewollt(false);
  }, [open, mode, initialVendor, initialProductId]);

  const hasUnsavedChanges = useMemo(() => {
    if (!open || status !== 'idle') return false;
    if (rows.length) return true;
    if (mode === 'new') {
      return Boolean(
        productName ||
        category ||
        baseNumber ||
        (vendorId && vendorId !== (initialVendor?.id || '')) ||
        modelValues.length ||
        sizeValues.length ||
        colorValues.length ||
        sourceUrls.trim() ||
        imageUrls.trim()
      );
    }
    return Boolean(
      productId ||
      modelValues.length ||
      sizeValues.length ||
      colorValues.length
    );
  }, [
    open,
    status,
    rows,
    mode,
    productName,
    category,
    baseNumber,
    vendorId,
    initialVendor,
    modelValues,
    sizeValues,
    colorValues,
    productId,
    sourceUrls,
    imageUrls,
  ]);

  const handleRequestClose = () => {
    // 🔴 Nach dem Erfolg ist das Schliessen die Quittung -- hier geht `onSuccess`
    // raus, nicht mehr in `finish()`. Sonst verschwindet der Dialog in demselben
    // Tick, in dem er „Done!" setzt, und „keine Meldung" ist von „nichts
    // passiert" nicht zu unterscheiden. Wer deshalb wiederholt, legt die
    // Operation ein ZWEITES Mal an. Gemessen am 2026-09-27.
    if (status === 'success') {
      onSuccess?.(successPayload);
      onClose();
      return;
    }
    if (hasUnsavedChanges) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  /* ----------------------------- basic-mode render ----------------------------- */
  // mode === 'basic' renders a flat Dialog with 6-7 fields and posts to
  // /api/products v2. Skips Options/Variants, SKU preview, AI enrichment
  // entirely. SKU is auto-generated; the operator never types one.
  if (mode === 'basic') {
    return (
      <Dialog
        open={open}
        onClose={basicSubmitting ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {sourceContext === 'po' ? 'New product' : 'Add product'}
          <IconButton
            onClick={onClose}
            disabled={basicSubmitting}
            aria-label="Close"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {caseProductReference && (
              <Alert severity="info" variant="outlined">
                <Typography variant="caption" color="text.secondary">
                  Creating replacement for:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {caseProductReference}
                </Typography>
              </Alert>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Vendor
              </Typography>
              {basicVendor ? (
                <Chip
                  label={basicVendor.vendor_name || basicVendor.name || basicVendor.id}
                  size="small"
                />
              ) : (
                <Typography variant="body2" color="error">
                  No vendor selected — caller must pass initialVendor.
                </Typography>
              )}
            </Box>

            <TextField
              size="small"
              fullWidth
              required
              label="Product name"
              value={basicForm.productName}
              onChange={(e) => handleBasicChange('productName', e.target.value)}
              disabled={basicSubmitting}
            />

            <VerticalSelect
              size="small"
              value={basicForm.verticalKey}
              error={basicVerticalError}
              helperText={basicVerticalError
                ? 'Required — pick a vertical before creating.'
                : undefined}
              onChange={(key) => {
                setBasicVerticalError(false);
                // Clear the Alert too — it carries the same demand, and in this
                // dialog both are on screen at once, so leaving it up beside a
                // now-satisfied field reads as "this is still wrong".
                //
                // But ONLY our own message: this setter also carries "Product
                // name, cost, vendor, and category are required" and server-side
                // create failures, and picking a vertical does not fix those.
                setBasicError((prev) => (prev.startsWith('Pick a vertical') ? '' : prev));
                handleBasicChange('verticalKey', key);
              }}
              disabled={basicSubmitting}
            />

            <TextField
              size="small"
              fullWidth
              label="Variant name (optional)"
              helperText="Defaults to product name when blank."
              value={basicForm.variantName}
              onChange={(e) => handleBasicChange('variantName', e.target.value)}
              disabled={basicSubmitting}
            />

            <FormControl
              size="small"
              fullWidth
              disabled={basicSubmitting}
              required
              error={categoriesStatus === 'error' && !ersatzlisteGreift}
            >
              {/* labelId, like the stepper's Category: without it the Select has
                  no accessible name at all. */}
              <InputLabel id="basic-category-label">Category</InputLabel>
              <Select
                labelId="basic-category-label"
                value={basicForm.category}
                label="Category"
                // 🔴 Explizit verdrahtet, nicht dem FormControl ueberlassen:
                // MUI 7.3.5 reicht `helperTextId` NICHT an den Select weiter —
                // gemessen am gerenderten `role="combobox"` traegt es
                // `aria-labelledby` + `aria-invalid`, aber KEIN
                // `aria-describedby` (Regel-16-Runde-2-W1). Ohne diese Zeile
                // bliebe der Hinweis fuer Screenreader hinter der Interaktion.
                aria-describedby={basicKategorieHilfeId}
                onChange={(e) => handleBasicChange('category', e.target.value)}
              >
                {/* 🔴 NUR EINES der vier Praedikate war gefaehrdet, und nur
                    dieses braucht `Boolean()`: `basicForm.verticalKey && …`
                    liefert bei leerem Feld `''` — fuer React.Children.toArray
                    ein gueltiges (String-)Kind ohne `props`, und MUI Select
                    stuerzt beim AUSWAEHLEN an `child.props.value` ab
                    (Regel-8-A/B 2026-10-13). Die drei anderen sind bereits
                    boolesch (`!…`, `=== 'loading'`, `=== 'brewout'`); ihr
                    frueheres `Boolean()` war redundant. */}
                {!basicForm.verticalKey && (
                  <MenuItem value="" disabled>Pick a vertical first</MenuItem>
                )}
                {Boolean(basicForm.verticalKey && basicForm.verticalKey !== 'brewout'
                  && categories.length === 0 && categoriesStatus === 'error') && (
                  <MenuItem value="" disabled>Categories could not be loaded</MenuItem>
                )}
                {categories.length === 0 && categoriesStatus === 'loading' && (
                  <MenuItem value="" disabled>Loading…</MenuItem>
                )}
                {/* 🔴 Die Kaffee-Ersatzliste gilt NUR fuer brewout (Grill-Runde 2,
                    W-Neu-2). Sie stand bis zum 2026-10-12 in jedem Dialog — ein
                    Messer-Formular bot damit "Kaffee-Mühlen" an, wenn das
                    Register gerade nicht antwortete. Fuer jede andere Vertikale
                    bleibt nur der benannte Fehlereintrag.
                    🔴 Und sie kommt aus `KAFFEE_ERSATZKATEGORIEN`, derselben
                    Konstante, gegen die der Riegel in `handleBasicSubmit`
                    prueft — sonst boete das Feld Werte an, die die Anlage
                    ablehnt (Regel-16-Runde-2-Blocker 1). */}
                {ersatzlisteGreift && KAFFEE_ERSATZKATEGORIEN.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
              {/* 🔴 Der Fehlerzustand steht nicht mehr NUR im geoeffneten Menue
                  (Regel-16-W3, WCAG 3.3.1): wer das Menue nicht oeffnet, sieht
                  am Feld sonst nichts von einem gescheiterten Register-Abruf.
                  🔴 Die Verknuepfung ans Feld ist EXPLIZIT (`aria-describedby`
                  am Select + `id` hier) — die frueher hier behauptete
                  Weiterreichung durch das FormControl gibt es in MUI 7.3.5
                  nicht (Regel-16-Runde-2-W1, am gerenderten Feld gemessen).
                  Derselbe Grund am Feld fuer den vertikalfreien Zustand. */}
              {!basicForm.verticalKey && (
                <FormHelperText id="basic-category-help-vertical">
                  Pick a vertical first — it decides which categories are offered.
                </FormHelperText>
              )}
              {/* 🔴 NICHT, wenn die Ersatzliste greift: dann widerspraeche sich
                  das Feld selbst — vier waehlbare Eintraege darueber, „could not
                  be loaded" darunter (Regel-16-Runde-2-W3). */}
              {categoriesStatus === 'error' && !ersatzlisteGreift && (
                <FormHelperText id="basic-category-help-error">
                  Categories could not be loaded.
                </FormHelperText>
              )}
            </FormControl>

            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                required
                label={kostenEtikett(sourceContext, poCurrency)}
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={basicForm.cost}
                onChange={(e) => handleBasicChange('cost', e.target.value)}
                disabled={basicSubmitting}
                sx={{ flex: 2 }}
              />
              {sourceContext !== 'po' && <FormControl size="small" sx={{ flex: 1 }} disabled={basicSubmitting}>
                <InputLabel id="basic-currency-label">Currency</InputLabel>
                <Select
                  value={basicForm.currency}
                  labelId="basic-currency-label"
                  label="Currency"
                  onChange={(e) => handleBasicChange('currency', e.target.value)}
                >
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>}
            </Stack>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createInLsmBasic}
                    onChange={(e) => setCreateInLsmBasic(e.target.checked)}
                    disabled={basicSubmitting}
                  />
                }
                label="Create product in LSM Control"
              />
              <FormHelperText sx={{ mt: 0 }}>
                {lsmHilfetext(sourceContext)}
              </FormHelperText>
            </Box>

            {/* PR2 (2026-10-27): der PO-Schnellweg reichert an (Owner „1. ja“). R16 W1:
                „requested“, nicht „starts“ — nach der Anlage kommt keine Quittung, die
                `n8n_triggered` auswertet. Kachel-Name wie in `UrgencyRow.jsx` (W7); das
                Fenster steht ohne Zahl, weil das Frontend `NEU_TAGE` nicht kennt. */}
            {sourceContext === 'po' && (
              <Alert severity="info" variant="outlined">
                AI enrichment is requested after creation. While data is still missing, the product is listed
                during its first days on the dashboard tile “New incomplete” and in the morning briefing.
              </Alert>
            )}

            {basicError && (
              <Alert severity="error">{basicError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={basicSubmitting}>
            Cancel
          </Button>
          <DisabledReason
            reason={basicSubmitting
              ? null
              : !basicVendor ? 'Select a vendor first.' : null}
          >
            <Button
              variant="contained"
              onClick={handleBasicSubmit}
              disabled={basicSubmitting || !basicVendor}
              startIcon={basicSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {basicSubmitting ? 'Creating…' : 'Create'}
            </Button>
          </DisabledReason>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <MobileSheet
      open={open}
      maxWidth="md"
      onClose={handleRequestClose}
    >
      <DialogTitle>
        {mode === 'new' ? 'Create Product' : 'Add Variants to Product'}
        {/* 🔴 „Close wizard", nicht „Close" wie die uebrigen 19 Dialogkreuze
            der App. Der Erfolgsbildschirm traegt einen eigenen Knopf „Close"
            (die Quittung, die `onSuccess` ausloest) — zwei gleich benannte
            Knoepfe im selben Dialog sind fuer einen Screenreader nicht
            unterscheidbar. Das Kreuz sagt jetzt, was es schliesst. */}
        <IconButton onClick={handleRequestClose} aria-label="Close wizard" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {status === 'idle' && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
            {steps.map((s) => (
              <Step key={s}><StepLabel>{stepLabel(s)}</StepLabel></Step>
            ))}
          </Stepper>
        )}

        {status === 'loading' && (
          <Grid container justifyContent="center" sx={{ my: 3 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2, alignSelf: 'center' }}>Saving…</Typography>
          </Grid>
        )}

        {status === 'error' && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
        {/* R16-W1: im Modus `new` ist die Anreicherung unbedingt — die Quittung
            sagt, in welchem Zustand sie ist. Belegt, nicht behauptet: der
            Rumpf sendet `status: 'DRAFT'` (`buildV2Payload` in
            `createNewProductAndMaybeVariants`), und WEDER der n8n-Ablauf
            (`docs/Custom Ai Product Writer.json`, kein Produkt-Status im
            productUpdate) NOCH `backend/routes/enrichment_api.py` setzen ihn
            um — das Produkt bleibt Draft, bis jemand es in Shopify aktiviert.
            Variantenweg und Basis ohne `sourceContext` reichern NICHT an; der PO-Schnellweg reichert an, erreicht diese Quittung aber nie (`mode === 'basic'` rendert vorher, sein Hinweis steht VOR der Anlage im Dialog).
            🔴 „running" NUR bei `n8n_triggered: true` in der Antwort
            (`successPayload`, gesetzt in `finish()`). Bei `false` hat
            `trigger_n8n_product_enrichment` den Webhook nicht zugestellt und
            den Lauf in `AIEnrichmentQueue` gelegt; der Takt
            `ai_enrichment_queue_retry` wiederholt ihn — begrenzt
            (`max_retries`), deshalb „will be retried", nicht „will start"
            (R16-W1-Rest, 2026-10-27). */}
        {status === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {mode !== 'new'
              ? 'Done!'
              : successPayload?.n8n_triggered
                ? 'Done! AI enrichment is running in the background — the product is created as Draft and stays Draft; activate it in Shopify once the content has arrived.'
                : 'Done! AI enrichment is queued and will be retried automatically — the product is created as Draft and stays Draft; activate it in Shopify once the content has arrived.'}
          </Alert>
        )}

        {status === 'idle' && (
          <>
            {renderStep()}
            <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                SKU preview
              </Typography>
              {skuPreviewData.mode === 'issued' ? (
                <Typography variant="body2" color="text.secondary">
                  Numbers are assigned by the server when the product is created.
                </Typography>
              ) : skuPreviewData.mode === 'base' ? (
                <Typography variant="body2" color="text.secondary">
                  Vendor {skuPreviewData.vendor} • Category {skuPreviewData.category} • Base {skuPreviewData.base}
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {/* Keyed by position, not by SKU: two option values sharing a
                      mapping code compose to the same number, and a duplicate
                      React key there is a warning nobody would connect to this
                      list. */}
                  {skuPreviewData.skus.slice(0, SKU_PREVIEW_LIMIT).map((sku, i) => (
                    <Chip key={`${i}-${sku}`} label={sku} size="small" />
                  ))}
                  {skuPreviewData.skus.length > SKU_PREVIEW_LIMIT && (
                    <Chip
                      label={`+${skuPreviewData.skus.length - SKU_PREVIEW_LIMIT} more`}
                      size="small"
                    />
                  )}
                </Stack>
              )}
            </Paper>
          </>
        )}
      </DialogContent>

      {/* A disabled Next used to be a dead control with the cause hidden inside
          whichever row failed — findable, but only by hunting. This names the
          count and points at the reasons, and gives the button something to be
          described by (WCAG 3.3.3). */}
      {steps[activeStep] === 'Options & Variants' && blockingRowCount > 0 && (
        <Typography
          id="sku-block-summary"
          variant="body2"
          // NOT warning.dark: #e65100 on white is 3.79:1 and this is 14px body
          // text, so AA wants 4.5:1. tokens.js `warnText` (#b45309, 5.02:1) is
          // the accessible warning-text tone this repo already ships.
          sx={(t) => ({ px: 3, pb: 1, color: v2Tone(t, 'warn').main })}
        >
          {blockingRowCount === 1
            ? '1 variant has no SKU yet — see the reason on its row.'
            : `${blockingRowCount} of ${rows.length} variants have no SKU yet — see the reasons on their rows.`}
        </Typography>
      )}

      <DialogActions>
        {status === 'idle' && (
          <>
            <Button disabled={activeStep === 0} onClick={back}>Back</Button>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={next}
                disabled={!canNext}
                aria-describedby={
                  steps[activeStep] === 'Options & Variants' && blockingRowCount > 0
                    ? 'sku-block-summary'
                    : undefined
                }
              >
                Next
              </Button>
            ) : (
              <DisabledReason
                reason={status === 'loading'
                  ? null
                  : !newVariantRows.length
                    ? 'Add at least one variant row.'
                    : newVariantRows.some(r => r.moq === '' || r.cost === '' || r.price === '')
                      ? 'Fill in MOQ, cost and price for every variant row.'
                      : (mode === 'new'
                        ? (!vendorId || !productName || !category || !baseNumber)
                          ? 'Fill in vendor, product name, category and base number.'
                          : null
                        : !productId ? 'Select a product first.' : null)}
              >
                <Button
                  variant="contained"
                  onClick={handleCreateClick}
                  disabled={
                    status === 'loading' ||
                    !newVariantRows.length ||
                    newVariantRows.some(r => r.moq === '' || r.cost === '' || r.price === '') ||
                    (mode === 'new'
                      ? (!vendorId || !productName || !category || !baseNumber)
                      : !productId
                    )
                  }
                >
                  Create
                </Button>
              </DisabledReason>
            )}
          </>
        )}

        {/* 🔴 Ein Fehlerschirm mit nur „Close" ist eine Sackgasse: der Inhalt
            haengt an `status === 'idle'`, also ist das Formular weg, und
            `hasUnsavedChanges` ist bei `status !== 'idle'` false — Close
            verwirft ohne Rueckfrage. Wer eine Meldung liest, die zum Handeln
            auffordert („set a price"), muss dorthin zurueckkommen koennen.
            Die Zeilen stehen noch im Zustand; nur `status` muss zurueck. */}
        {status === 'error' && (
          <Button onClick={() => { setStatus('idle'); setErrorMsg(''); }}>
            Back to the form
          </Button>
        )}
        {status !== 'idle' && (
          <Button variant="contained" onClick={handleRequestClose}>Close</Button>
        )}
      </DialogActions>
      {/* Add Mapping Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
        <DialogTitle>Add {addType} mapping</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Value (label)"
              fullWidth
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              autoFocus
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Assigned code:
              </Typography>
              <Chip label={addCode || '—'} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!addType || !addLabel || addCode.length !== 2) return;
              const code2 = addCode.padStart(2, '0');
              try {
                if (addType === 'model') {
                  const { data } = await axios.post(`${API}/api/sku-model-mappings`, { model: addLabel, model_code: code2 });
                  setModelMap((prev) => [...prev, data]);
                  setModelValues((prev) => [...prev, data]);
                } else if (addType === 'size') {
                  const { data } = await axios.post(`${API}/api/sku-size-mappings`, { size: addLabel, size_code: code2 });
                  setSizeMap((prev) => [...prev, data]);
                  setSizeValues((prev) => [...prev, data]);
                } else if (addType === 'color') {
                  const { data } = await axios.post(`${API}/api/sku-color-mappings`, { color: addLabel, color_code: code2 });
                  setColorMap((prev) => [...prev, data]);
                  setColorValues((prev) => [...prev, data]);
                }
                setAddOpen(false);
                setAddLabel('');
              } catch (err) {
                console.error(err);
                setErrorMsg('Failed to add mapping value.');
              }
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single→multi colour prompt (grill decision C7, Task 7 of the
          productSet-unification plan): fires when the target product is
          still a Shopify placeholder (no real colour axis) and the operator
          picked a new colour to add. Confirming submits straight into
          finish() so the operator doesn't have to click Create twice. */}
      <Dialog open={colourPromptOpen} onClose={() => setColourPromptOpen(false)}>
        <DialogTitle>What colour is the existing variant?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              This product only has a single variant with no colour option yet.
              Adding {colorValues.length > 1 ? 'these colours' : 'this colour'} will
              introduce a Colour option — tell us what the existing variant already
              is so it isn't left without a value.
            </Typography>
            {colorMap.length === 0 ? (
              <Alert severity="warning">
                No colour mappings exist yet. Cancel and use “+ Add value” on the
                Colour axis first, then retry.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Existing variant colour</InputLabel>
                <Select
                  value={colourPromptValue}
                  label="Existing variant colour"
                  onChange={(e) => setColourPromptValue(e.target.value)}
                >
                  {colorMap.map((c) => (
                    <MenuItem key={c.color} value={c.color}>{c.color}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColourPromptOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!colourPromptValue}
            onClick={() => {
              setSoleVariantColour(colourPromptValue);
              setColourPromptOpen(false);
              finish(colourPromptValue);
            }}
          >
            Confirm &amp; create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmCloseOpen} onClose={() => setConfirmCloseOpen(false)}>
        <DialogTitle>Discard product creation?</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved progress in the wizard. Are you sure you want to close it?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCloseOpen(false)}>Continue editing</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmCloseOpen(false);
              onClose();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </MobileSheet>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * PR2 PO-Schnellweg (2026-10-27) — Herkunft, Anreicherung und LSM-Hinweis des
 * Basismodus. Plan: docs/superpowers/plans/2026-10-27-po-schnellweg-
 * vollstaendigkeit.md, Rev. 2 W5, Rev. 2.1 W-A/W-D.
 *
 * 🔴 AM DATEIENDE, und der Dialog oben wurde nur ZEILE GEGEN ZEILE geaendert:
 * auf diese Datei zeigen ueber 300 dokumentierte `UnifiedCreateWizard.jsx:NNN`-
 * Verweise (Regel 11 §„Eine ZEILENKOORDINATE ist kein Text“). Oben kam dazu:
 *   * die Props `sourceContext` und `poId` (in der Zeile von `defaultCategory`).
 *     🔴 `sourceContext` hat KEINEN Standard `'po'` (W-D): nur `PODetail.jsx`
 *     setzt ihn. Ersatzteil-Dialog und RMA-Auswahl setzen ihn nicht — fuer sie
 *     bleibt der Basismodus, was er war (LSM aus, keine Anreicherung, keine
 *     Herkunft). `poId` ist `null` in einer noch nicht gespeicherten PO.
 *   * `createInLsmBasic` startet mit `sourceContext === 'po'` (W5): die PO lief
 *     bis hierher ueber den vollen Assistenten, und der legt den LSM-Artikel
 *     standardmaessig an. Ein blosses Umschalten auf den Basismodus haette
 *     PO-Produkte still OHNE Lagerartikel angelegt. Der Startwert liest die Prop
 *     beim MOUNT; jeder Aufrufer ist eine eigene Instanz. Der Haken bleibt
 *     sichtbar und abwaehlbar.
 *   * beide Zweige von `buildPayload` nehmen `herkunftUndAnreicherung(…)` statt
 *     des festen `skip_n8n_enrichment: true`.
 *   * `onSuccess` traegt `product_id` (W-A) — in BEIDEN Antwortformen steht sie
 *     oben (Neuanlage aus `_finalize_created_product`, Replay im
 *     Idempotenz-Zweig); `null`, wenn sie fehlt, nie geraten — und `moq: 1`, die
 *     Menge, mit der `buildPayload` die Variante anlegt.
 *   * der Hilfetext unter dem LSM-Haken kommt aus `lsmHilfetext`.
 *   * (R16, 27.10.) die Prop `poCurrency` (Zeile von `defaultCategory`); im
 *     PO-Weg der Dialogtitel „New product“ (H2), die Waehrung FEST aus der PO
 *     (`wirksameWaehrung` auch in `onSuccess`, W3) und der Hinweis ohne
 *     Behauptung „starts“ (W1).
 *   * (R16 Runde 2 C1) im PO-Weg KEIN zweites Waehrungsfeld: die Waehrung steht
 *     im Etikett des Kostenfelds (`kostenEtikett`, „Cost (USD)“); die Auswahl
 *     daneben rendert nur ohne `sourceContext === 'po'`.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Herkunft und Anreicherung der Basis-Nutzlast.
 *
 * Owner auf die Rückfrage „KI-Anreicherung auch im PO-Schnellweg?“ (27.10.):
 * „1. ja“ — dieselbe Form wie der Assistent, `baueAnreicherungsFelder({})`
 * (`enabled: true`, leere Listen; der Workflow recherchiert über Name und
 * Hersteller). Ohne `sourceContext` (Ersatzteil, RMA) bleibt es bei
 * `skip_n8n_enrichment: true` ohne `ai_enrichment` (Owner-Entscheid A).
 *
 * @param {string|null} sourceContext  `'po'` aus der Bestellung, sonst `null`
 * @param {string|null} poId           die Bestellnummer, falls die PO gespeichert ist
 * @returns {object} die Felder, die in die Nutzlast gespreizt werden
 */
function herkunftUndAnreicherung(sourceContext, poId) {
  return {
    ...(sourceContext === 'po' ? baueAnreicherungsFelder({}) : { skip_n8n_enrichment: true }),
    ...(sourceContext ? { source_context: sourceContext } : {}),
    ...(poId ? { po_id: poId } : {}),
  };
}

/** Der Hilfetext unter dem LSM-Haken — in der PO startet er AN (W5). */
function lsmHilfetext(sourceContext) {
  return sourceContext === 'po'
    ? 'On by default for products added from a purchase order, so the goods can be received. Untick to skip the article in LSM Control (warehouse).'
    : 'Off by default for ad-hoc spare adds. Tick to also create this product as an article in LSM Control (warehouse).';
}

/**
 * Die Waehrung des Basisdialogs (R16 W3, Entscheid Orchestrator 27.10.).
 *
 * Im PO-Schnellweg gilt die Waehrung der BESTELLUNG, fest: `zeileAusNeuemProdukt`
 * uebernimmt `cost_per_item` als PO-Waehrung, und bis hierher startete der Dialog
 * mit waehlbarem `EUR` — in einer USD-PO entstand so still ein falscher Betrag.
 * Ersatzteil-Dialog und RMA-Auswahl (ohne `sourceContext`) behalten die Auswahl.
 *
 * @param {string|null} sourceContext
 * @param {string|null} poCurrency  `currency` der PO (`PODetail.jsx`)
 * @param {string} [gewaehlt]       die Auswahl im Dialog (nur ohne PO)
 */
function wirksameWaehrung(sourceContext, poCurrency, gewaehlt = 'EUR') {
  return sourceContext === 'po' ? (poCurrency || 'EUR') : gewaehlt;
}

/**
 * Das Etikett des Kostenfelds (R16 Runde 2 C1).
 *
 * Im PO-Schnellweg steht die Waehrung der Bestellung IM Etikett — „Cost (USD)“,
 * dasselbe Muster wie die Preisfelder der Bestellzeilen in `PODetail.jsx`
 * (`Price (${currency})`). Bis Runde 2 stand daneben ein zweites, nur lesbares
 * Feld „PO currency“: es sah aus wie ein Eingabefeld, war ein Tab-Stopp, und ein
 * Klick tat nichts. Eine Beschriftung kann man nicht fuer bedienbar halten.
 * Ersatzteil-Dialog und RMA-Auswahl (ohne `sourceContext`) behalten „Cost“ und
 * die Waehrungsauswahl daneben.
 *
 * @param {string|null} sourceContext
 * @param {string|null} poCurrency  `currency` der PO (`PODetail.jsx`)
 * @returns {string}
 */
function kostenEtikett(sourceContext, poCurrency) {
  return sourceContext === 'po' ? `Cost (${wirksameWaehrung(sourceContext, poCurrency)})` : 'Cost';
}
